import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from "@vaiu/ui/button";
import { Input } from "@vaiu/ui/input";
import { Label } from "@vaiu/ui/label";
import { Checkbox } from "@vaiu/ui/checkbox";
import { InfoIcon, HelpCircle, ChevronDown, ChevronUp, MapPin, Home, CheckCircle2, Loader2, Circle, Plus, Minus, X, Sparkles } from "lucide-react";
import { i18n } from '@/utils/i18n';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@vaiu/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@vaiu/ui/select";
import TokenCard, { Token, TokenTheme } from './TokenCard';
import TopUpBalance from './TopUpBalance';
import { CurrencyDisplay } from '@vaiu/ui';
// import TokenTypeSlider from './TokenTypeSlider';  // Commented out - replaced by token mode dropdown (Give money, Request money, ID Token)
import CreateIDToken from './CreateIDToken';
import type { TokenPlatformPayload } from '@vaiu/types';
import TokenConditioning, {
  TokenConditions,
  buildValidityRedemptionExtensions,
  buildEscrowActivatePayload,
  DEFAULT_TOKEN_CONDITIONS,
} from '@vaiu/token-ux';
import StripeProvider from './payment/StripeProvider';
import { CardToTokenFlow } from './payment/CardToTokenFlow';
import { useToast } from '@/hooks/use-toast';
import { platformApi } from '@/services/platformApi';
import { currencyService } from '@/services/currencyService';
import { roundMonetaryValue, sanitizeAmount } from '@vaiu/utils/currency';
import {
  isUnfundedTokenStatus,
  resolvePayPalCallbackBaseUrl,
  isFramedWindow,
  subscribePayPalFundingResult,
  publishPayPalFundingResult,
  isPayPalFundingReturnTab,
  readPayPalSessionId,
} from '@vaiu/utils';
import { isMmMoneyCurrency, getSpendableBalance, isOwnMmMoneyCurrency, resolveAuthUserId, withOwnMmMoneyBalance, formatMmMoneyDisplayTicker, parseMmMoneyIssuerId, type MmMoneyIssuerView } from '@vaiu/utils/mmMoney';
import { MmMoneyIssuerDisplay } from '@vaiu/cash-token-card';
import { useAuth } from '@/context/AuthContext';
import { useConfig } from '@/context/ConfigContext';
import { emitTokenCreationBalanceUpdate, emitBalanceUpdateSignal, onBalanceUpdate, emitTokenCreated, TokenCreatedDetail } from '@/utils/lightEventEmitter';
import { useNFCBridge } from '@/hooks/useNFCBridge';
import { isCardToTokenEnabled } from '@/config/cardToTokenConfig';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@vaiu/ui/dialog";
import {
  AccordionContent,
  AccordionTrigger,
} from "@vaiu/ui/accordion";


interface Address {
  id: string;
  lat: number;
  lng: number;
  address: string;
  detail: string;
  isPrimary?: boolean;
}

interface CreateCashTokenProps {
  typeOfToken?: 'cash' | 'invoice';  // Strongly typed token types
  selectedCurrency: string;
  balances: { [key: string]: number };
  /** `true` always show ticker select; `false` lock to `selectedCurrency` (POS / checkout with currency or cart); omit to auto-enable for signed-in cash/invoice tokens. */
  allowCurrencyChange?: boolean;
  tokenTheme?: TokenTheme;
  onCancel?: () => void;
  onSubmit: (token: Token) => void; // Only called when token is successfully created
  isSubmitting?: boolean; // Made optional, component manages its own loading state
  
  // New props for PayPage integration
  initialAmount?: string;
  initialMessage?: string;
  autoSubmit?: boolean;
  showBalanceInfo?: boolean;
  merchantId?: string;  // For auto-deposit to merchant
  payeeName?: string;  // Name of the payee (merchant or user)
  isGuest?: boolean; // Explicitly pass if the user is a guest
  
  // New prop for unified slider UI
  showTypeSlider?: boolean;
  onTypeChange?: (type: 'cash' | 'invoice') => void;
  isPersonalMode?: boolean;  // New prop to pass down to TokenTypeSlider
  embedMode?: boolean;  // For embedded iframe experience
  postToParent?: boolean; // If true, post token to parent window via postMessage
  
  // Token display control
  showTokenAfterCreation?: boolean; // If true (default), show TokenCard internally. If false, parent handles display
  onTokenDismiss?: () => void; // Called when token card is dismissed/closed internally
  
  // PayPal cleanup callback - pages can call this when they clear token state
  // This ensures PayPal params are cleaned up even if page manages its own token display
  onPayPalCleanup?: () => void; // Called when PayPal params should be cleaned up
  
  // Ref to expose cleanup function to parent components
  cleanupPayPalParamsRef?: React.MutableRefObject<(() => void) | null>; // Optional ref to expose cleanup function
  
  // Ref to expose reset function - allows parent to reset token creation state
  resetTokenCreationRef?: React.MutableRefObject<(() => void) | null>; // Optional ref to expose reset function

  /** Merged into activate `payload` when present. Type is {@link TokenPlatformPayload} (any platform shape). */
  extraTokenPayload?: TokenPlatformPayload;

  /** Controlled token mode when type selector is rendered outside this component (e.g. accordion header). */
  tokenMode?: 'give' | 'request' | 'id';
  onTokenModeChange?: (mode: 'give' | 'request' | 'id') => void;
  /** Hide the in-body type dropdown (header hosts the selector). */
  hideTypeSelector?: boolean;
}

/** HTML `step` for `<input type="number">` from max decimal places. */
function decimalsToHtmlStep(maxDecimals: number): string {
  if (maxDecimals <= 0) return '1';
  return `0.${'0'.repeat(Math.max(0, maxDecimals - 1))}1`;
}

/** Restrict typing/paste to the currency's allowed fractional digits (0 = integers only). */
function sanitizeAmountInput(raw: string, maxDecimals: number): string {
  if (raw === '') return '';
  if (maxDecimals === 0) {
    const cleaned = raw.replace(/[^\d.]/g, '');
    const n = parseFloat(cleaned);
    if (Number.isNaN(n)) return '';
    return String(Math.max(0, Math.trunc(n)));
  }
  let s = raw.replace(/[^\d.]/g, '');
  if (s === '') return '';
  const firstDot = s.indexOf('.');
  if (firstDot === -1) {
    return s.replace(/^0+(?=\d)/, '') || s;
  }
  let intPart = s.slice(0, firstDot).replace(/\D/g, '');
  let frac = s.slice(firstDot + 1).replace(/\D/g, '');
  frac = frac.slice(0, maxDecimals);
  intPart = intPart.replace(/^0+(?=\d)/, '');
  const endsWithDot = s.endsWith('.') && frac.length === 0;
  if (endsWithDot) {
    return `${intPart || '0'}.`;
  }
  if (frac.length > 0) {
    return `${intPart || '0'}.${frac}`;
  }
  return intPart;
}

export type TokenMode = 'give' | 'request' | 'id';

interface TokenModeSelectProps {
  value: TokenMode;
  onValueChange: (value: TokenMode) => void;
  triggerClassName?: string;
  stopPropagation?: boolean;
}

export function TokenModeSelect({
  value,
  onValueChange,
  triggerClassName = 'w-[11rem] sm:w-[12.5rem]',
  stopPropagation = false,
}: TokenModeSelectProps) {
  const stop = (e: React.SyntheticEvent) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
  };

  return (
    <div onClick={stop} onPointerDown={stop} onKeyDown={stop}>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className={triggerClassName} onClick={stop} onPointerDown={stop}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="give">{i18n.__('vaiu.token_mode.give_money')}</SelectItem>
          <SelectItem value="request">{i18n.__('vaiu.token_mode.request_money')}</SelectItem>
          <SelectItem value="id">{i18n.__('vaiu.token_mode.id_token')}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

interface CreatedTokenPreviewProps {
  token: Token;
  theme?: TokenTheme;
  onDismiss?: () => void;
  className?: string;
  showQRCode?: boolean;
}

function CreatedTokenResult({
  token,
  theme = 'default',
  onDismiss,
  showQRCode = true,
}: CreatedTokenPreviewProps) {
  const previewRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [token.key]);

  return (
    <div ref={previewRef} className="space-y-3">
      <CreatedTokenPreview
        token={token}
        theme={theme}
        onDismiss={onDismiss}
        className="px-0 py-0 border-0 bg-transparent"
        showQRCode={showQRCode}
      />
      {onDismiss ? (
        <Button type="button" variant="outline" className="w-full" onClick={onDismiss}>
          {i18n.__('core.createAnotherToken')}
        </Button>
      ) : null}
    </div>
  );
}

export function CreatedTokenPreview({
  token,
  theme = 'default',
  onDismiss,
  className = '',
  showQRCode = true,
}: CreatedTokenPreviewProps) {
  return (
    <div className={`px-6 py-3 border-b border-border/60 bg-muted/20 ${className}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-sm font-medium text-muted-foreground">
          {token.type === 'invoice'
            ? i18n.__('core.recentlyCreatedInvoice')
            : i18n.__('core.recentlyCreatedToken')}
        </p>
        {onDismiss ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className="h-8 w-8 p-0 shrink-0"
            aria-label="Dismiss created token"
          >
            <X className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
      <TokenCard
        token={token}
        theme={theme}
        showActions={true}
        showQRCode={showQRCode}
        qrMode="button"
        showPrepaidCardOption={true}
        className="shadow-none border-0"
        borderless={true}
      />
    </div>
  );
}

interface CreateTokenShellProps {
  recentToken?: Token | null;
  onRecentTokenDismiss?: () => void;
  tokenMode: TokenMode;
  onTokenModeChange: (mode: TokenMode) => void;
  theme?: TokenTheme;
  children: React.ReactNode;
}

/** Accordion shell: header, then either the created token or the form. */
export function CreateTokenShell({
  recentToken,
  onRecentTokenDismiss,
  tokenMode,
  onTokenModeChange,
  theme = 'default',
  children,
}: CreateTokenShellProps) {
  return (
    <>
      <AccordionTrigger className="text-left hover:no-underline px-6 py-4">
        <div className="flex w-full items-center justify-between gap-3 pr-2">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-5 h-5 shrink-0" />
            <span className="text-lg font-semibold">{i18n.__('vaiu.token_slider.create_token')}</span>
          </div>
          {!recentToken ? (
            <TokenModeSelect
              value={tokenMode}
              onValueChange={onTokenModeChange}
              stopPropagation
            />
          ) : null}
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <div className="px-6 pb-4">
          {recentToken ? (
            <CreatedTokenResult
              token={recentToken}
              theme={theme}
              onDismiss={onRecentTokenDismiss}
            />
          ) : null}
          <div className={recentToken ? 'hidden' : undefined}>{children}</div>
        </div>
      </AccordionContent>
    </>
  );
}

const CreateToken = ({
  typeOfToken = 'cash', 
  selectedCurrency,
  balances,
  allowCurrencyChange,
  tokenTheme = 'default',
  onCancel, 
  onSubmit,
  isSubmitting: externalIsSubmitting = false,
  
  // New props with defaults
  initialAmount = '',
  initialMessage = '',
  autoSubmit = false,
  showBalanceInfo = true,
  merchantId,
  payeeName,
  isGuest = false, // Default to false if not provided
  
  // Slider props
  showTypeSlider = false,
  onTypeChange,
  isPersonalMode = true,
  embedMode = false,
  postToParent = false, // Default to false - will be detected from URL if not provided
  
  // Token display props
  showTokenAfterCreation = true, // Default to showing token internally
  onTokenDismiss,
  onPayPalCleanup,
  cleanupPayPalParamsRef,
  resetTokenCreationRef,
  extraTokenPayload,
  tokenMode: controlledTokenMode,
  onTokenModeChange,
  hideTypeSelector = false,
}: CreateCashTokenProps) => {
  // Removed console.log to prevent log spam - uncomment for debugging if needed
  // console.log('🔍 CreateCashToken component mounted with props:', {
  //   merchantId,
  //   typeOfToken,
  //   selectedCurrency,
  //   initialAmount,
  //   initialMessage,
  //   showBalanceInfo
  // });
  
  const { toast } = useToast();
  const { user, isGuest: isUserGuest } = useAuth();
  const { config } = useConfig();
  const authUserId = resolveAuthUserId(user);
  const effectiveBalances = useMemo(
    () => withOwnMmMoneyBalance(balances, authUserId),
    [balances, authUserId]
  );
  const spendableBalance = useCallback(
    (currency: string) => getSpendableBalance(effectiveBalances, currency, authUserId),
    [effectiveBalances, authUserId]
  );

  const fetchMmMoneyMerchantSummary = useCallback(async (issuerUserId: string) => {
    try {
      const p = await platformApi.merchant.getPublicMerchantProfile(issuerUserId);
      return {
        displayName: p.businessName || p.contactName,
        username: (p as { username?: string }).username,
        avatarUrl: p.logo,
        bio: p.description,
      };
    } catch {
      return null;
    }
  }, []);

  // Check if user has BANK role privileges
  // BANK and ADMIN have infinite money for create (platform prepaid cover)
  const isBankOrAdminUser =
    Boolean(user?.roles?.includes('BANK')) || Boolean(user?.roles?.includes('ADMIN'));
  const isBankUser = isBankOrAdminUser;
  const isMerchant = user?.roles?.includes('MERCHANT');
  
  // NFC Bridge and Card-to-Token integration
  const { isAvailable: nfcAvailable, isChecking: nfcChecking } = useNFCBridge();
  const featureEnabled = isCardToTokenEnabled();
  const [showCardToTokenDialog, setShowCardToTokenDialog] = useState(false);
  
  // Internal token type state for slider (kept for non-slider mode compatibility)
  const [internalTokenType, setInternalTokenType] = useState<'cash' | 'invoice'>(typeOfToken);
  
  // Token mode: give (cash), request (invoice), or id (identity token) - used when showTypeSlider
  const [localTokenMode, setLocalTokenMode] = useState<TokenMode>('give');
  const tokenMode = controlledTokenMode ?? localTokenMode;

  const handleTokenModeChange = (v: TokenMode) => {
    if (controlledTokenMode === undefined) {
      setLocalTokenMode(v);
    }
    onTokenModeChange?.(v);
    if (v === 'request') {
      setInternalTokenType('invoice');
      setIsReferralToken(false);
      onTypeChange?.('invoice');
    } else if (v === 'give') {
      setInternalTokenType('cash');
      onTypeChange?.('cash');
    }
  };
  
  // Use tokenMode when showTypeSlider: give=cash, request=invoice. Use internal type or prop otherwise.
  const effectiveTokenType = showTypeSlider
    ? (tokenMode === 'request' ? 'invoice' : 'cash')
    : (showTypeSlider ? internalTokenType : typeOfToken);
  
  // Form state - initialize with props if provided
  const [amount, setAmount] = useState(initialAmount);
  const [message, setMessage] = useState(initialMessage);
  const [currency, setCurrency] = useState(selectedCurrency);
  const canSelectTokenCurrency =
    allowCurrencyChange === true
      ? true
      : allowCurrencyChange === false
        ? false
        : // Cash: signed-in only (funding/balances). Invoice: always when unlocked (no balance required).
          effectiveTokenType === 'invoice' || !isGuest;
  // Funding, balance validation, and token creation must use the currency selected beside the amount.
  const effectiveCurrency = canSelectTokenCurrency ? currency : selectedCurrency;
  const selectableCurrencies = useMemo(() => {
    const currencies = new Set([selectedCurrency, ...Object.keys(effectiveBalances)]);
    // Invoice requests are not limited to balances you already hold — include platform-supported tickers.
    if (effectiveTokenType === 'invoice') {
      for (const c of config?.ui?.supportedCurrencies ?? []) {
        if (c) currencies.add(c);
      }
    }
    return [...currencies].sort((left, right) => {
      const leftIsMmMoney = isMmMoneyCurrency(left);
      const rightIsMmMoney = isMmMoneyCurrency(right);
      if (leftIsMmMoney !== rightIsMmMoney) return leftIsMmMoney ? 1 : -1;
      if (!leftIsMmMoney) return spendableBalance(right) - spendableBalance(left);
      return left.localeCompare(right);
    });
  }, [selectedCurrency, effectiveBalances, spendableBalance, effectiveTokenType, config?.ui?.supportedCurrencies]);

  const [amountDecimals, setAmountDecimals] = useState(() =>
    isMmMoneyCurrency(selectedCurrency) ? 0 : 2
  );

  useEffect(() => {
    let cancelled = false;
    void currencyService.getCurrencyDetails(effectiveCurrency).then((det) => {
      if (cancelled) return;
      const d = Math.max(0, Math.min(18, Number.isFinite(det.decimals) ? det.decimals : 2));
      setAmountDecimals(d);
      setAmount((prev) => {
        if (prev === '') return prev;
        const n = parseFloat(prev);
        if (Number.isNaN(n)) return prev;
        const r = roundMonetaryValue(n, d);
        if (d === 0) return String(Math.max(0, Math.trunc(r)));
        const fixed = parseFloat(r.toFixed(d)).toString();
        return fixed;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [effectiveCurrency]);

  const [selectedMmIssuerMeta, setSelectedMmIssuerMeta] = useState<Partial<MmMoneyIssuerView> | null>(null);

  useEffect(() => {
    if (!isMmMoneyCurrency(effectiveCurrency) || isOwnMmMoneyCurrency(effectiveCurrency, authUserId)) {
      setSelectedMmIssuerMeta(null);
      return;
    }
    const issuerId = parseMmMoneyIssuerId(effectiveCurrency);
    if (!issuerId) {
      setSelectedMmIssuerMeta(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { merchants } = await platformApi.getPublicMerchantProfilesBatch([issuerId]);
        if (cancelled) return;
        const m = merchants.find((x) => x.id === issuerId);
        if (m) {
          const biz = (m.businessName || '').trim();
          const contact = (m.contactName || '').trim();
          setSelectedMmIssuerMeta({
            issuerUserId: issuerId,
            displayName: biz || undefined,
            name: contact && contact !== biz ? contact : undefined,
            currencyIconUrl: m.mmMoneyBranding?.currencyIconUrl || m.logo,
          });
        } else {
          setSelectedMmIssuerMeta({ issuerUserId: issuerId });
        }
      } catch {
        if (!cancelled) setSelectedMmIssuerMeta({ issuerUserId: issuerId });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveCurrency, authUserId]);

  const amountFieldCurrencyLabel = useMemo(() => {
    if (!isMmMoneyCurrency(effectiveCurrency)) {
      return effectiveCurrency;
    }
    if (isOwnMmMoneyCurrency(effectiveCurrency, authUserId)) {
      return formatMmMoneyDisplayTicker();
    }
    return null;
  }, [effectiveCurrency, authUserId]);

  const amountRounded = useMemo(() => {
    if (!amount || amount === '.') return null;
    const n = parseFloat(amount);
    if (Number.isNaN(n) || n < 0) return null;
    return roundMonetaryValue(n, amountDecimals);
  }, [amount, amountDecimals]);
  
  // Referral token state
  const [isReferralToken, setIsReferralToken] = useState(false);
  const [referralTokensCreated, setReferralTokensCreated] = useState(0);
  const [loadingReferralLimits, setLoadingReferralLimits] = useState(false);
  const [isMultiUseToken, setIsMultiUseToken] = useState(false);
  const [maxUses, setMaxUses] = useState('');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  const parsedMaxUses = useMemo(() => {
    const trimmed = maxUses.trim();
    if (!trimmed) return undefined;
    const n = parseInt(trimmed, 10);
    if (Number.isNaN(n) || n < 2) return undefined;
    return n;
  }, [maxUses]);

  /** Prepaid cover for create / top-up (face × maxUses when multi-use). */
  const prepaidRequiredAmount = useMemo(() => {
    if (amountRounded == null) return 0;
    if (isMultiUseToken && parsedMaxUses) {
      return roundMonetaryValue(amountRounded * parsedMaxUses, amountDecimals);
    }
    return amountRounded;
  }, [amountRounded, isMultiUseToken, parsedMaxUses, amountDecimals]);

  // Path A tip: load policy when advanced options open (consumers never mint Path B here unless bank/admin)
  const [pathAPolicyEnabled, setPathAPolicyEnabled] = useState(false);
  const [pathAPolicyHint, setPathAPolicyHint] = useState('');

  useEffect(() => {
    if (!showAdvancedOptions || isGuest) return;
    let cancelled = false;
    (async () => {
      try {
        const { referralApi } = await import('@/services/referralApi');
        const dash = await referralApi.getUserReferralDashboard().catch(() => null);
        const cfg = dash?.referralConfig;
        if (cancelled || !cfg) return;
        setPathAPolicyEnabled(Boolean(cfg.enabled));
        if (cfg.enabled && (cfg.rewardAmount ?? 0) > 0) {
          setPathAPolicyHint(
            `If someone redeems your cash token (≥ ${cfg.minimumTokenAmount || 0} ${cfg.rewardCurrency || ''}) as their first redemption in that currency, you may earn ${cfg.rewardAmount} ${cfg.rewardCurrency}.`
          );
        } else {
          setPathAPolicyHint('');
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showAdvancedOptions, isGuest]);

  // Show message input if there's an initial message, otherwise hide by default to save space
  const [showMessageInput, setShowMessageInput] = useState(!!initialMessage);
  
  // Token conditioning state
  const [showConditioning, setShowConditioning] = useState(false);
  const [tokenConditions, setTokenConditions] = useState<TokenConditions | null>(null);
  
  // Address state
  const [userAddresses, setUserAddresses] = useState<Address[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string>('');
  const [includeAddress, setIncludeAddress] = useState(false);
  const [addressesLoading, setAddressesLoading] = useState(false);
  
  // Internal loading state
  const [internalIsSubmitting, setInternalIsSubmitting] = useState(false);
  
  // Use internal loading state or external one (defined early so it's available for useCallback)
  const isSubmitting = internalIsSubmitting || externalIsSubmitting;
  
  // TopUp state - now managed internally
  const [showTopUpDialog, setShowTopUpDialog] = useState(0);
  const [topUpRequiredAmount, setTopUpRequiredAmount] = useState(0);
  const [topUpCurrency, setTopUpCurrency] = useState(selectedCurrency);
  const [selectedFundingSource, setSelectedFundingSource] = useState<'balance' | 'card' | 'bank' | 'paypal' | 'wallet' | 'bitcoin' | 'ethereum'>('card');
  const [topUpInitialTab, setTopUpInitialTab] = useState<'card' | 'digital-wallet' | 'paypal'>('card');
  const [pendingTokenData, setPendingTokenData] = useState<{
    amount: number;
    message: string;
    isReferralToken: boolean;
    isMultiUseToken?: boolean;
    maxUses?: number;
    tokenConditions?: TokenConditions;
    tokenKey?: string;
  } | null>(null);
  
  // Inline payment state for seamless guest flow
  const [showInlinePayment, setShowInlinePayment] = useState(false);
  const [paymentStage, setPaymentStage] = useState<'idle' | 'funding' | 'creating-token' | 'complete'>('idle');
  
  // Internal token state - CreateCashToken manages its own token display
  const [createdToken, setCreatedToken] = useState<Token | null>(null);


  // Guest receipt contact info
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  // Auto-submit functionality
  const autoSubmitAttemptedRef = useRef(false);
  const mountedRef = useRef(false);
  const processedPayPalReturnRef = useRef<string>(''); // Track processed PayPal returns to prevent duplicates
  const paypalProcessingRef = useRef(false); // Track if PayPal return is currently being processed
  const tokenCreatedFromPayPalRef = useRef(false); // Track if token was created from PayPal return
  const tokenCreatedRef = useRef(false); // Track if ANY token was created (even if showTokenAfterCreation=false)
  const processingBalanceUpdateRef = useRef(false); // Track if balance update handler is currently processing
  const createTokenRef = useRef<
    | ((
        params: {
          amount: number;
          message: string;
          isReferralToken: boolean;
          isMultiUseToken?: boolean;
          maxUses?: number;
          overrideCurrency?: string;
          conditionsOverride?: TokenConditions;
        }
      ) => Promise<void>)
    | null
  >(null); // Ref to createToken function
  const tokenCreatedEventEmittedRef = useRef(false); // Track if token:created event was already emitted (prevent duplicates)
  const location = useLocation();
  
  // Detect postToParent from URL if not provided as prop (use existing queryParams from cleanupPayPalParams)
  const queryParams = new URLSearchParams(location.search);
  const detectedPostToParent = postToParent || queryParams.get('postToParent') === 'true' || queryParams.get('ptp') === 'true';
  
  // Helper function to clean up PayPal URL parameters
  // This is called internally by CreateCashToken to handle all PayPal param cleanup
  // Also exposed to parent via ref for pages that manage their own token display
  const cleanupPayPalParams = useCallback(() => {
    const currentParams = new URLSearchParams(location.search);
    const hasPayPalParams = currentParams.has('paypal') || currentParams.has('context');
    
    if (hasPayPalParams) {
      console.log('🧹 [CreateCashToken] Cleaning up PayPal URL parameters');
      currentParams.delete('paypal');
      currentParams.delete('context');
      const cleanUrl = location.pathname + (currentParams.toString() ? `?${currentParams.toString()}` : '');
      window.history.replaceState({}, '', cleanUrl);
      
      // Also clean up sessionStorage
      try {
        sessionStorage.removeItem('paypalReturnProcessed');
      } catch (e) {
        console.warn('⚠️ Failed to clean sessionStorage:', e);
      }
      
      // Notify parent that cleanup happened (for pages that manage their own token display)
      onPayPalCleanup?.();
    }
  }, [location.search, location.pathname, onPayPalCleanup]);
  
  // Function to reset token creation state - allows creating new token after closing previous one
  const resetTokenCreation = useCallback(() => {
    console.log('🔄 [CreateCashToken] Resetting token creation state');
    tokenCreatedRef.current = false;
    tokenCreatedEventEmittedRef.current = false; // Reset event emission flag to allow new token events
    setCreatedToken(null);
    
    // CRITICAL: Also clean up PayPal params and mark as processed when resetting
    // This prevents any remaining PayPal params from triggering duplicate token creation
    const currentParams = new URLSearchParams(location.search);
    const paypalStatus = currentParams.get('paypal');
    const paypalContext = currentParams.get('context');
    if (paypalStatus || paypalContext) {
      console.log('🧹 [CreateCashToken] Cleaning up PayPal params during reset');
      const paypalReturnKey = `${paypalStatus}-${paypalContext || ''}`;
      processedPayPalReturnRef.current = paypalReturnKey;
      cleanupPayPalParams();
    }
  }, [location.search, cleanupPayPalParams]);
  
  // Expose cleanup and reset functions via refs if provided
  useEffect(() => {
    if (cleanupPayPalParamsRef) {
      cleanupPayPalParamsRef.current = cleanupPayPalParams;
    }
    if (resetTokenCreationRef) {
      resetTokenCreationRef.current = resetTokenCreation;
    }
  }, [cleanupPayPalParamsRef, cleanupPayPalParams, resetTokenCreationRef, resetTokenCreation]);

  const refreshCreatedToken = useCallback(async (tokenKey: string): Promise<Token | null> => {
    try {
      const details = await platformApi.getTokenDetails(tokenKey);
      const t = details?.token;
      if (!t) return null;
      const updated: Token = {
        key: t.key || tokenKey,
        amount: Math.abs(t.amount || 0),
        currency: t.currency,
        status: t.status,
        expiry: t.expiresAt || '',
        message: t.message,
        type: (t.type as Token['type']) || 'cash',
      };
      if (showTokenAfterCreation) {
        setCreatedToken(updated);
      }
      if (!isUnfundedTokenStatus(updated.status)) {
        tokenCreatedRef.current = true;
        try {
          localStorage.removeItem('pendingTokenCreation');
        } catch {
          /* ignore */
        }
        setPendingTokenData(null);
        setShowTopUpDialog(0);
        setShowInlinePayment(false);
        if (!tokenCreatedEventEmittedRef.current) {
          emitTokenCreated({
            token: {
              key: updated.key,
              amount: updated.amount,
              currency: updated.currency,
              message: updated.message,
              type: updated.type,
              ...updated,
            },
          });
          tokenCreatedEventEmittedRef.current = true;
        }
        onSubmit(updated);
        if (detectedPostToParent && window.parent && window.parent !== window) {
          try {
            window.parent.postMessage(
              {
                type: 'vaiu',
                token: updated.key,
                amount: updated.amount,
                currency: updated.currency,
                message: updated.message,
              },
              '*'
            );
          } catch {
            /* ignore */
          }
        }
      }
      return updated;
    } catch (error) {
      console.warn('Failed to refresh token after PayPal return:', error);
      return null;
    }
  }, [onSubmit, showTokenAfterCreation, detectedPostToParent]);

  useEffect(() => {
    if (!isFramedWindow()) return undefined;
    return subscribePayPalFundingResult((payload) => {
      let expected = '';
      try {
        expected = sessionStorage.getItem('paypalFundingSessionId') || '';
      } catch {
        expected = '';
      }
      if (expected && payload.sessionId !== expected) return;
      if (payload.type === 'vaiu-paypal-funded' && payload.tokenKey) {
        void refreshCreatedToken(payload.tokenKey);
        emitBalanceUpdateSignal();
      }
    });
  }, [refreshCreatedToken]);

  // Reusable function to attempt token creation from localStorage
  // This is called both on mount and when balance:update event is received
  // On mount: Always checks localStorage, updates UI state, and attempts token creation if spec exists
  // On balance:update: Checks localStorage or state, then attempts token creation
  const attemptTokenCreationFromLocalStorage = useCallback(async (source: 'mount' | 'balance:update') => {
    // CRITICAL: Prevent duplicate processing
    if (processingBalanceUpdateRef.current) {
      console.log(`💰 [${source}] Already processing, skipping duplicate`);
      return;
    }

    // Use internalIsSubmitting || externalIsSubmitting directly since isSubmitting is defined later
    const isCurrentlySubmitting = internalIsSubmitting || externalIsSubmitting;

    let storedTokenKey: string | undefined;
    try {
      const storedPending = localStorage.getItem('pendingTokenCreation');
      if (storedPending) {
        storedTokenKey = JSON.parse(storedPending)?.tokenKey;
      }
    } catch {
      storedTokenKey = undefined;
    }

    if (storedTokenKey && source === 'balance:update') {
      processingBalanceUpdateRef.current = true;
      try {
        await refreshCreatedToken(storedTokenKey);
      } finally {
        processingBalanceUpdateRef.current = false;
      }
      return;
    }

    // CRITICAL: Don't create token if already created or in progress
    // Exception: On mount, we still want to update UI state even if token was already created
    if (source !== 'mount' && (tokenCreatedRef.current || isCurrentlySubmitting) && !storedTokenKey) {
      console.log(`💰 [${source}] Token already created or creation in progress`, {
        tokenCreated: tokenCreatedRef.current,
        isSubmitting: isCurrentlySubmitting
      });
      return;
    }

    console.log(`🔍 [${source}] Checking localStorage for pending token`);

    // Read pending token data from localStorage
    let tokenDataFromStorage: {
      amount: number;
      message: string;
      currency: string;
      isReferralToken: boolean;
      isMultiUseToken?: boolean;
      maxUses?: number;
      tokenConditions?: TokenConditions;
    } | null = null;
    try {
      const stored = localStorage.getItem('pendingTokenCreation');
      const tokenSpec = stored ? JSON.parse(stored) : null;
      console.log(`🔍 [${source}] Checking localStorage for pending token:`, {
        hasTokenSpec: !!tokenSpec,
        tokenSpec: tokenSpec
      });
      
      if (stored) {
        const parsed = JSON.parse(stored);
        const ageMs = parsed.timestamp ? Date.now() - parsed.timestamp : Infinity;
        const ageMinutes = ageMs / (60 * 1000);
        
        console.log(`🔍 [${source}] Parsed localStorage data:`, {
          parsed,
          ageMs,
          ageMinutes: ageMinutes.toFixed(2),
          isStale: ageMs >= 60 * 60 * 1000
        });
        
        // Check if data is not too old (e.g., within 1 hour)
        if (parsed.timestamp && ageMs < 60 * 60 * 1000) {
          const tc = parsed.tokenConditions as TokenConditions | undefined;
          tokenDataFromStorage = {
            amount: parsed.amount,
            message: parsed.message || '',
            currency: parsed.currency,
            isReferralToken: parsed.isReferralToken || false,
            isMultiUseToken: parsed.isMultiUseToken || parsed.isReferralToken || false,
            ...(typeof parsed.maxUses === 'number' ? { maxUses: parsed.maxUses } : {}),
            ...(tc ? { tokenConditions: tc } : {}),
          };
          console.log(`✅ [${source}] Using token data from localStorage:`, tokenDataFromStorage);
          
          // On mount: Always update UI state from localStorage if data exists
          // This ensures UI reflects the token spec even if component remounts
          if (source === 'mount') {
            console.log(`💾 [${source}] Restoring pending token creation from localStorage:`, tokenDataFromStorage);
            setPendingTokenData({
              amount: tokenDataFromStorage.amount,
              message: tokenDataFromStorage.message,
              isReferralToken: tokenDataFromStorage.isReferralToken,
              isMultiUseToken: tokenDataFromStorage.isMultiUseToken,
              ...(tokenDataFromStorage.maxUses ? { maxUses: tokenDataFromStorage.maxUses } : {}),
              ...(tokenDataFromStorage.tokenConditions
                ? { tokenConditions: tokenDataFromStorage.tokenConditions }
                : {}),
            });
            setTopUpRequiredAmount(tokenDataFromStorage.amount);
            setTopUpCurrency(tokenDataFromStorage.currency);
            
            // Restore form fields for UI display. Prefer `amount` from the current URL so changing
            // query params (e.g. /pay?amount=2) is not overridden by a stale pending spec in localStorage.
            const fromUrl = sanitizeAmount(
              new URLSearchParams(location.search).get('amount'),
              8,
              false
            );
            if (fromUrl != null) {
              setAmount(fromUrl.toString());
            } else {
              setAmount(tokenDataFromStorage.amount.toString());
            }
            if (tokenDataFromStorage.message) {
              setMessage(tokenDataFromStorage.message);
            }
            if (tokenDataFromStorage.tokenConditions) {
              setTokenConditions(tokenDataFromStorage.tokenConditions);
              setShowConditioning(true);
            }
            if (tokenDataFromStorage.isReferralToken) {
              setIsReferralToken(true);
            }
            if (tokenDataFromStorage.isMultiUseToken) {
              setIsMultiUseToken(true);
            }
            if (tokenDataFromStorage.maxUses) {
              setMaxUses(String(tokenDataFromStorage.maxUses));
            }
          }
        } else {
          // Data is stale, remove it
          localStorage.removeItem('pendingTokenCreation');
          console.log(`🧹 [${source}] Removed stale pending token data from localStorage (age:`, ageMinutes.toFixed(2), 'minutes)');
          // On mount, if data is stale, we're done (no token to create)
          // On balance:update, continue to check state as fallback
          if (source === 'mount') {
            return;
          }
        }
      } else {
        console.log(`⚠️ [${source}] No token spec found in localStorage`);
        // On mount, if no localStorage data, we're done (no token to create)
        // On balance:update, continue to check state as fallback
        if (source === 'mount') {
          return;
        }
      }
    } catch (e) {
      console.warn(`⚠️ [${source}] Failed to read pending token from localStorage:`, e);
      // On mount, if error reading localStorage, we're done
      // On balance:update, continue to check state as fallback
      if (source === 'mount') {
        return;
      }
    }
    
    // Use localStorage data if available, otherwise fall back to state
    // On mount: tokenDataFromStorage should be set if localStorage had valid data
    // On balance:update: can use either localStorage or state
    const tokenDataToUse = tokenDataFromStorage || pendingTokenData;
    
    // Only proceed if we have pending token data (user was trying to create a token)
    if (!tokenDataToUse) {
      console.log(`💰 [${source}] No pending token creation`, {
        hasStorageData: !!tokenDataFromStorage,
        hasStateData: !!pendingTokenData
      });
      return;
    }

    // On mount: If token was already created, skip creation attempt (UI state already updated above)
    // On balance:update: This check was already done at the start, but double-check here too
    if (tokenCreatedRef.current || isCurrentlySubmitting) {
      console.log(`💰 [${source}] Token already created or creation in progress, skipping creation attempt`, {
        tokenCreated: tokenCreatedRef.current,
        isSubmitting: isCurrentlySubmitting,
        uiStateUpdated: source === 'mount' && !!tokenDataFromStorage
      });
      return;
    }

    // Mark as processing
    processingBalanceUpdateRef.current = true;

    // CRITICAL: Fetch fresh balances from platform instead of relying on props
    // This ensures we have the latest balance even if parent component hasn't updated yet
    const pendingCurrency = tokenDataToUse.currency || topUpCurrency || selectedCurrency;
    let currentBalance = spendableBalance(pendingCurrency);
    
    try {
      console.log(`💰 [${source}] Fetching fresh balances from platform`);
      const freshBalances = withOwnMmMoneyBalance(await platformApi.getBalances(), authUserId);
      currentBalance = getSpendableBalance(freshBalances, pendingCurrency, authUserId);
      console.log(`✅ [${source}] Fetched fresh balances:`, {
        freshBalances,
        pendingCurrency,
        currentBalance
      });
    } catch (error) {
      console.warn(`⚠️ [${source}] Failed to fetch fresh balances, using prop balance:`, error);
      // Fall back to prop balance if fetch fails
      currentBalance = spendableBalance(pendingCurrency);
    }

    console.log(`💰 [${source}] Checking balance for token creation:`, {
      requestedAmount: tokenDataToUse.amount,
      currency: pendingCurrency,
      currentBalance,
      sufficient: currentBalance >= tokenDataToUse.amount,
      tokenDataSource: tokenDataFromStorage ? 'localStorage' : 'state'
    });

    // Check if balance is sufficient
    if (currentBalance >= tokenDataToUse.amount) {
      console.log(`✅ [${source}] Balance sufficient, automatically creating token`);
      
      // Retry token creation automatically
      try {
        // CRITICAL: Mark as creating to prevent duplicate attempts
        tokenCreatedFromPayPalRef.current = true;
        
        // Use ref to call createToken to avoid dependency order issues
        if (!createTokenRef.current) {
          console.error(`❌ [${source}] createToken not available yet`);
          processingBalanceUpdateRef.current = false;
          return;
        }
        
        await createTokenRef.current({
          amount: tokenDataToUse.amount,
          message: tokenDataToUse.message,
          isReferralToken: tokenDataToUse.isReferralToken,
          isMultiUseToken: tokenDataToUse.isMultiUseToken,
          maxUses: tokenDataToUse.maxUses,
          overrideCurrency: pendingCurrency,
          conditionsOverride: tokenDataToUse.tokenConditions,
        });
        
        console.log(`✅ [${source}] Token created successfully`);
        
        // CRITICAL: tokenCreatedRef.current is set in createToken success handler
        // Clear pending token data from both state and localStorage
        setPendingTokenData(null);
        setTopUpRequiredAmount(0);
        try {
          localStorage.removeItem('pendingTokenCreation');
          console.log(`🧹 [${source}] Cleared pending token creation from localStorage`);
        } catch (e) {
          console.warn(`⚠️ [${source}] Failed to clear localStorage:`, e);
        }
      } catch (error: any) {
        console.error(`❌ [${source}] Token creation failed:`, error);
        
        const isBalanceError = error?.message?.toLowerCase().includes('insufficient') ||
                              error?.message?.toLowerCase().includes('balance');
        
        if (isBalanceError && !isOwnMmMoneyCurrency(pendingCurrency, authUserId)) {
          // If API says insufficient, show topup UI (don't retry)
          // Note: localStorage is already set from handleSubmit, no need to set it again here
          console.log(`💳 [${source}] API reported insufficient balance, showing topup UI`);
          
          setPendingTokenData({ 
            amount: tokenDataToUse.amount, 
            message: tokenDataToUse.message, 
            isReferralToken: tokenDataToUse.isReferralToken,
            isMultiUseToken: tokenDataToUse.isMultiUseToken,
            ...(tokenDataToUse.maxUses ? { maxUses: tokenDataToUse.maxUses } : {}),
            ...(tokenDataToUse.tokenConditions
              ? { tokenConditions: tokenDataToUse.tokenConditions }
              : {}),
          });
          setTopUpRequiredAmount(tokenDataToUse.amount);
          setTopUpCurrency(pendingCurrency);
          setShowTopUpDialog(1);
        } else {
          toast({
            title: 'Creation Failed',
            description: 'Token creation failed. Please try again or contact support.',
            variant: 'destructive'
          });
        }
      } finally {
        // Reset processing flag
        processingBalanceUpdateRef.current = false;
      }
    } else {
      console.log(`⚠️ [${source}] Balance still insufficient:`, {
        required: tokenDataToUse.amount,
        available: currentBalance,
        shortfall: tokenDataToUse.amount - currentBalance
      });
      
      // Note: localStorage is already set from handleSubmit, no need to set it again here
      // Show topup UI
      setPendingTokenData({ 
        amount: tokenDataToUse.amount, 
        message: tokenDataToUse.message, 
        isReferralToken: tokenDataToUse.isReferralToken,
        isMultiUseToken: tokenDataToUse.isMultiUseToken,
        ...(tokenDataToUse.maxUses ? { maxUses: tokenDataToUse.maxUses } : {}),
        ...(tokenDataToUse.tokenConditions
          ? { tokenConditions: tokenDataToUse.tokenConditions }
          : {}),
      });
      setTopUpRequiredAmount(tokenDataToUse.amount);
      setTopUpCurrency(pendingCurrency);
      setShowTopUpDialog(1);
      
      // Reset processing flag
      processingBalanceUpdateRef.current = false;
    }
  }, [pendingTokenData, topUpCurrency, selectedCurrency, balances, internalIsSubmitting, externalIsSubmitting, toast, setPendingTokenData, setTopUpRequiredAmount, setTopUpCurrency, setShowTopUpDialog, location.search, refreshCreatedToken]);

  // On mount: Check localStorage for token spec and attempt token creation
  // The function will fetch fresh balances internally before making token creation decisions
  // This ensures we have up-to-date balance data before making any token creation decisions
  // This works independently of balance:update event - component checks localStorage whenever it mounts
  // This ensures token creation works correctly when component mounts after PayPal return or any other scenario
  useEffect(() => {
    console.log('🔍 [Mount] CreateCashToken mounted, checking localStorage for pending token spec');
    attemptTokenCreationFromLocalStorage('mount');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - attemptTokenCreationFromLocalStorage is stable via useCallback

  useEffect(() => {
    if (isReferralToken) {
      setIsMultiUseToken(true);
    }
  }, [isReferralToken]);

  // Load referral token limits only when referral checkbox is enabled
  useEffect(() => {
    const loadReferralLimits = async () => {
      try {
        setLoadingReferralLimits(true);
        // Use typed client method; BaseApiClient already prefixes /api/v1
        const response = await platformApi.getReferralDashboard() as any;
        if (response?.tokenLimits) {
          setReferralTokensCreated(response.tokenLimits.tokensCreated || 0);
        }
      } catch (error) {
        console.error('Failed to load referral limits:', error);
        // Don't show error toast for this, just log it
      } finally {
        setLoadingReferralLimits(false);
      }
    };

    // Only load for cash tokens and only when referral tokens are enabled
    if (effectiveTokenType === 'cash' && isReferralToken) {
      loadReferralLimits();
    }
  }, [effectiveTokenType, isReferralToken]); // Use effectiveTokenType instead of typeOfToken

  // For guests: automatically set pending token data when amount is entered
  // CRITICAL: This must set localStorage (via stashTokenSpecAndShowTopup) so token data persists after PayPal redirect
  useEffect(() => {
    if (isGuest && amount && parseFloat(amount) > 0) {
      console.log('💳 Guest entered amount, preparing pending token data');
      
      // SECURITY: This data is only used for UI state
      // The backend MUST validate all values independently
      const tokenAmount = roundMonetaryValue(parseFloat(amount), amountDecimals);
      
      // Validate amount is reasonable (client-side validation for UX only)
      if (tokenAmount > 0 && tokenAmount <= 10000) {
        const tokenMessage = message || `Payment of ${amount} ${selectedCurrency}`;
        
        // CRITICAL: Call stashTokenSpecAndShowTopup to set localStorage AND state
        // This ensures token data persists even if component remounts after PayPal redirect
        stashTokenSpecAndShowTopup(tokenAmount, tokenMessage, selectedCurrency, false);
        
        // Set UI state for inline payment flow
        if (!showInlinePayment) {
          setShowInlinePayment(true);
        }
        if (paymentStage !== 'idle') {
          setPaymentStage('idle');
        }
      } else if (tokenAmount > 10000) {
        // Prevent unreasonable amounts (UX protection)
        console.warn('Amount exceeds reasonable limit');
        toast({
          title: 'Amount Too Large',
          description: 'Please enter an amount less than 10,000',
          variant: 'destructive'
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest, amount, amountDecimals]); // Only depend on isGuest and amount, not message/selectedCurrency to prevent loops

  // Function to build full return URL (origin + port + path + query params)
  // Uses React Router's location to ensure we get the correct current path
  const buildFullReturnUrl = useCallback(() => {
    return resolvePayPalCallbackBaseUrl();
  }, []);

  // Function to stash token spec and prepare topup UI
  const stashTokenSpecAndShowTopup = useCallback((
    tokenAmount: number,
    tokenMessage: string,
    tokenCurrency: string,
    isReferral: boolean,
    multiUse = false,
    multiUseMax?: number,
    requiredCoverAmount = tokenAmount,
  ) => {
    // CRITICAL: Store token specs in localStorage
    // This ensures token data persists even if user navigates away or component unmounts
    // Only store for cash tokens (not invoice tokens) since invoices don't require balance
    if (effectiveTokenType === 'cash') {
      try {
        const tokenDataToStore = {
          amount: tokenAmount,
          message: tokenMessage,
          currency: tokenCurrency,
          isReferralToken: isReferral,
          isMultiUseToken: multiUse || isReferral,
          ...(multiUseMax ? { maxUses: multiUseMax } : {}),
          timestamp: Date.now(),
          ...(showConditioning && tokenConditions
            ? { tokenConditions }
            : {}),
        };
        localStorage.setItem('pendingTokenCreation', JSON.stringify(tokenDataToStore));
        console.log('💾 Stored pending token creation in localStorage:', tokenDataToStore);
      } catch (e) {
        console.warn('⚠️ Failed to store pending token in localStorage:', e);
      }
    }
    
    // Set pending token data so TopUpBalance can use it
    setPendingTokenData({
      amount: tokenAmount,
      message: tokenMessage,
      isReferralToken: isReferral,
      isMultiUseToken: multiUse || isReferral,
      ...(multiUseMax ? { maxUses: multiUseMax } : {}),
      ...(showConditioning && tokenConditions
        ? { tokenConditions }
        : {}),
    });
    setTopUpRequiredAmount(requiredCoverAmount);
    setTopUpCurrency(tokenCurrency);
  }, [effectiveTokenType, showConditioning, tokenConditions]);

  // For authenticated users: set pending token data before any external funding,
  // or when an existing-balance token needs more funds.
  useEffect(() => {
    if (!isGuest && !isBankUser && effectiveTokenType === 'cash') {
      if (isOwnMmMoneyCurrency(effectiveCurrency, authUserId)) {
        if (pendingTokenData || topUpRequiredAmount > 0) {
          setPendingTokenData(null);
          setTopUpRequiredAmount(0);
          try {
            localStorage.removeItem('pendingTokenCreation');
          } catch (e) {
            console.warn('⚠️ Failed to clear pending token from localStorage:', e);
          }
        }
        return;
      }
      const tokenAmount = amountRounded ?? 0;
      const currentBalance = spendableBalance(effectiveCurrency);
      const requiredCover = prepaidRequiredAmount > 0 ? prepaidRequiredAmount : tokenAmount;
      const needsPreparedFunding =
        tokenAmount > 0 &&
        (selectedFundingSource !== 'balance' || requiredCover > currentBalance);

      if (!needsPreparedFunding) {
        if (pendingTokenData || topUpRequiredAmount > 0) {
          setPendingTokenData(null);
          setTopUpRequiredAmount(0);
          try {
            localStorage.removeItem('pendingTokenCreation');
          } catch (e) {
            console.warn('⚠️ Failed to clear pending token from localStorage:', e);
          }
        }
        return;
      }

      if (!pendingTokenData || pendingTokenData.amount !== tokenAmount || topUpRequiredAmount !== requiredCover) {
          // Process message with address if includeAddress is selected (same logic as handleSubmit)
          let processedMessage = message || '';
          if (includeAddress && selectedAddressId) {
            const selectedAddress = userAddresses.find(addr => addr.id === selectedAddressId);
            if (selectedAddress) {
              const addressText = selectedAddress.detail 
                ? `${selectedAddress.address}, ${selectedAddress.detail}`
                : selectedAddress.address;
              
              // Add address to message with clear formatting
              processedMessage = processedMessage 
                ? `${processedMessage}\n\n📍 Delivery Address:\n${addressText}`
                : `📍 Delivery Address:\n${addressText}`;
            }
          }
          
          stashTokenSpecAndShowTopup(
            tokenAmount,
            processedMessage,
            effectiveCurrency,
            isReferralToken,
            isMultiUseToken,
            parsedMaxUses,
            requiredCover,
          );
      }
    }
  }, [isGuest, isBankUser, effectiveTokenType, amount, amountRounded, message, isReferralToken, isMultiUseToken, parsedMaxUses, effectiveCurrency, selectedFundingSource, pendingTokenData, topUpRequiredAmount, prepaidRequiredAmount, stashTokenSpecAndShowTopup, includeAddress, selectedAddressId, userAddresses, authUserId, spendableBalance]);

  // Load user addresses when advanced options are shown
  useEffect(() => {
    const loadAddresses = async () => {
      if (!showAdvancedOptions || addressesLoading || userAddresses.length > 0) return;
      
      try {
        setAddressesLoading(true);
        const profileData = await platformApi.getProfile();
        if (profileData?.addresses && Array.isArray(profileData.addresses)) {
          setUserAddresses(profileData.addresses);
          // Auto-select primary address if available
          const primaryAddress = profileData.addresses.find((addr: Address) => addr.isPrimary);
          if (primaryAddress) {
            setSelectedAddressId(primaryAddress.id);
          }
        }
      } catch (error) {
        console.error('Failed to load addresses:', error);
        // Don't show error toast, addresses are optional
      } finally {
        setAddressesLoading(false);
      }
    };

    if (showAdvancedOptions) {
      loadAddresses();
    }
  }, [showAdvancedOptions]);

  // Keep amount in sync when the parent URL/query-driven initialAmount changes (e.g. ?amount=1 → ?amount=2)
  useEffect(() => {
    setAmount(typeof initialAmount === 'string' ? initialAmount : '');
  }, [initialAmount]);

  useEffect(() => {
    if (initialMessage && initialMessage !== message) {
      setMessage(initialMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage]); // Only depend on initialMessage prop, not internal state

  // Token creation logic - moved BEFORE useEffect hooks that use it
  const createToken = useCallback(async ({ 
    amount, 
    message, 
    isReferralToken,
    isMultiUseToken: multiUseFlag = false,
    maxUses: maxUsesOverride,
    overrideBalance, // Optional: use this balance instead of balances prop for event emission
    overrideCurrency, // Optional: use this currency instead of effectiveCurrency (for PayPal returns)
    /** When set (e.g. resumed from localStorage), use instead of React state — avoids stale null. */
    conditionsOverride,
  }: { 
    amount: number; 
    message: string; 
    isReferralToken: boolean;
    isMultiUseToken?: boolean;
    maxUses?: number;
    overrideBalance?: number; // Optional override for balance when we know the actual balance
    overrideCurrency?: string; // Optional override for currency (e.g., from PayPal context)
    conditionsOverride?: TokenConditions;
  }) => {
    if (!mountedRef.current) return;
    
    setInternalIsSubmitting(true);
    try {
      // For invoice tokens, send negative amounts to the API
      const apiAmount = effectiveTokenType === 'invoice' ? -Math.abs(amount) : amount;
      
      console.log(`🧾 Creating ${typeOfToken} token with amount: ${amount} -> API amount: ${apiAmount}`);
      
      // Determine if we should attempt auto-funding
      // For regular users: don't auto-fund, show top-up dialog if insufficient balance
      // For bank users: the backend should allow negative balance based on role, not this flag
      const shouldAutoFund = false; // Currently disabled - could be enabled based on user preferences
      
      // Use overrideCurrency if provided (e.g., from PayPal context), otherwise use effectiveCurrency
      // CRITICAL: Define tokenCurrency BEFORE using it in console.log
      const tokenCurrency = overrideCurrency || effectiveCurrency;
      
      const shouldMultiUse = multiUseFlag || isReferralToken;

      console.log('🔍 Token creation request:', {
        amount: apiAmount,
        currency: tokenCurrency,
        effectiveCurrency,
        overrideCurrency: overrideCurrency || 'none',
        selectedCurrency,
        message,
        fundAsNeeded: shouldAutoFund,
        isReferralToken,
        isMultiUseToken: shouldMultiUse,
        maxUses: maxUsesOverride,
        isBankUser, // Log for debugging, but backend should check role independently
      });
      
      // Build token parameters with conditions according to platform API spec
      const tokenParams: any = {
        amount: apiAmount,
        currency: tokenCurrency, // Use overrideCurrency if provided, otherwise effectiveCurrency
        message,
        fundAsNeeded: shouldAutoFund, // This is for auto-charging payment methods, NOT for bank privileges
        isReferralToken, // Pass referral token flag to API
      };

      if (shouldMultiUse) {
        if (maxUsesOverride && maxUsesOverride >= 2) {
          tokenParams.maxUses = maxUsesOverride;
        } else if (isReferralToken) {
          tokenParams.maxUses = 10;
        }
      }

      if (extraTokenPayload && Object.keys(extraTokenPayload).length > 0) {
        tokenParams.payload = { ...extraTokenPayload };
      }

      // Add auto-deposit parameters if merchantId is provided
      if (merchantId) {
        console.log('💳 Submitting payment for auto-deposit tomerchant:', merchantId);
        tokenParams.recipientUserId = merchantId;  // The merchant's userId
        tokenParams.autoProcess = true;  // Enable auto-deposit
        // Note: Not setting forceInbox - let autoProcess handle the flow
      } else {
        console.log('⚠️ No merchantId provided, creating regular token without auto-deposit');
      }
      
      // Add guest receipt contact info if provided
      if (isGuest) {
        if (guestEmail) {
          tokenParams.guestEmail = guestEmail;
        }
        if (guestPhone) {
          tokenParams.guestPhone = guestPhone;
        }
      }
      
      console.log('📤 Sending token params to API:', tokenParams);

      // Redemption conditions: validity.start/end ISO + optional redeemer + location (platform API)
      const effectiveConditions =
        conditionsOverride !== undefined ? conditionsOverride : tokenConditions;
      if (effectiveConditions && effectiveConditions.timeLimit) {
        const now = new Date();
        const endDate = new Date(
          now.getTime() + effectiveConditions.timeLimit * 24 * 60 * 60 * 1000
        );
        tokenParams.validity = buildValidityRedemptionExtensions(effectiveConditions, {
          start: now,
          end: endDate,
        });
      }

      // Add delivery address to payload if selected
      if (includeAddress && selectedAddressId) {
        const selectedAddress = userAddresses.find(addr => addr.id === selectedAddressId);
        if (selectedAddress) {
          tokenParams.payload = {
            ...tokenParams.payload,
            deliveryAddress: {
              id: selectedAddress.id,
              address: selectedAddress.address,
              detail: selectedAddress.detail || '',
              coordinates: {
                lat: selectedAddress.lat,
                lng: selectedAddress.lng
              },
              isPrimary: selectedAddress.isPrimary || false
            }
          };
          console.log('📍 Added delivery address to token payload:', selectedAddress.address);
        }
      }

      const escrowPayload = buildEscrowActivatePayload(effectiveConditions);
      if (escrowPayload) {
        tokenParams.payload = {
          ...tokenParams.payload,
          ...escrowPayload,
        };
      }

      const result = await platformApi.createToken(tokenParams);
      
      console.log('🔍 Raw API result:', result);
      console.log('🔍 Raw API result type:', typeof result);
      console.log('🔍 Raw API result keys:', result ? Object.keys(result) : 'null/undefined');
      console.log('📦 recipientDelivery in response:', result?.recipientDelivery);
      console.log('🎯 Auto-deposit status:', result?.recipientDelivery?.status);
      
      // Safely extract token data with fallbacks - handle empty API response
      if (!result || typeof result !== 'object') {
        console.error('❌ Invalid API response: empty or malformed data', result);
        throw new Error('Invalid API response: empty or malformed data');
      }

      // Cast to any to handle unexpected API response structures
      const apiResult = result as any;

      // Check if we have the expected response structure
      // Try multiple possible response formats
      const hasKey = apiResult.key || apiResult.tokenKey || apiResult.code;
      const hasTokenObj = apiResult.tokenObj || apiResult.token;
      
      console.log('🔍 Response structure check:', {
        hasKey: !!hasKey,
        hasTokenObj: !!hasTokenObj,
        key: apiResult.key || apiResult.tokenKey || apiResult.code,
        tokenObj: apiResult.tokenObj || apiResult.token,
        allKeys: Object.keys(apiResult)
      });

      if (!hasKey && !hasTokenObj) {
        console.error('🚨 Unexpected API response structure:', JSON.stringify(result, null, 2));
        
        // If the API returns completely empty, it's likely an insufficient balance issue
        const isEmpty = Object.keys(apiResult).length === 0;
        if (isEmpty) {
          console.log('💳 Empty API response detected - likely insufficient balance');
          throw new Error('insufficient_balance');
        }
        
        // Try to extract token key from alternative structures
        const alternativeKey = apiResult.data?.key || apiResult.data?.tokenKey || apiResult.data?.code;
        if (alternativeKey) {
          console.log('✅ Found token key in alternative structure:', alternativeKey);
          apiResult.key = alternativeKey;
        } else {
          throw new Error('Invalid API response: missing token data. Response: ' + JSON.stringify(result));
        }
      }

      // Debug logging for amount extraction
      console.log('🔍 Amount debugging:');
      console.log('  Original amount (from form):', amount);
      console.log('  apiResult.tokenObj?.data?.amount:', apiResult.tokenObj?.data?.amount);
      console.log('  apiResult.amount:', apiResult.amount);
      
      const extractedAmount = apiResult.tokenObj?.data?.amount || apiResult.amount || amount;
      console.log('  Extracted amount (before Math.abs):', extractedAmount);
      console.log('  Final amount (after Math.abs):', Math.abs(extractedAmount));

      // Extract token key from multiple possible locations
      const tokenKey = apiResult.key || apiResult.tokenKey || apiResult.code || apiResult.data?.key || apiResult.data?.tokenKey || apiResult.data?.code;
      
      // Extract token data from multiple possible structures
      const tokenData = apiResult.tokenObj?.data || apiResult.token?.data || apiResult.data || apiResult.tokenObj || apiResult.token || apiResult;
      
      const newToken: Token = {
        key: tokenKey || 'unknown',
        amount: Math.abs(tokenData?.amount || apiResult.amount || amount), // Use original amount if API doesn't return it
        currency: tokenData?.currency || apiResult.currency || tokenCurrency, // Use tokenCurrency (with override support)
        status: tokenData?.status || apiResult.tokenObj?.status || apiResult.status || apiResult.token?.status || 'active',
        expiry: apiResult.expiresAt || apiResult.expires || tokenData?.expiresAt || '',
        message: tokenData?.message || apiResult.message || message,
        type: effectiveTokenType, // Store the token type
        isInvoice: effectiveTokenType === 'invoice', // Flag for invoice tokens
        isReferralToken: isReferralToken, // Flag for referral tokens
        // Funding source tracking removed
      };

      if (isUnfundedTokenStatus(newToken.status) && effectiveTokenType === 'cash') {
        tokenCreatedRef.current = true;
        setInternalIsSubmitting(false);
        if (showTokenAfterCreation) {
          setCreatedToken(newToken);
        }
        try {
          const existing = localStorage.getItem('pendingTokenCreation');
          const parsed = existing ? JSON.parse(existing) : {};
          localStorage.setItem('pendingTokenCreation', JSON.stringify({
            ...parsed,
            amount: newToken.amount,
            currency: newToken.currency,
            message: newToken.message,
            tokenKey: newToken.key,
            timestamp: Date.now(),
          }));
        } catch {
          /* ignore */
        }
        setPendingTokenData({
          amount: newToken.amount,
          message: newToken.message || '',
          isReferralToken,
          tokenKey: newToken.key,
        });
        setTopUpRequiredAmount(newToken.amount);
        setTopUpCurrency(newToken.currency);
        if (selectedFundingSource === 'balance' && !isFramedWindow()) {
          setSelectedFundingSource('paypal');
          setTopUpInitialTab('paypal');
        }
        if (isGuest) {
          setShowInlinePayment(true);
          setPaymentStage('idle');
          setShowTopUpDialog(0);
        } else {
          setShowTopUpDialog(1);
        }
        return newToken;
      }

      console.log('✅ Token created successfully:', newToken);
      console.log('✅ Token key extracted:', tokenKey);
      console.log('✅ Token data extracted:', tokenData);
      
      // CRITICAL: Check if the API response includes balance information
      // This helps detect if the backend properly deducted the balance
      if (apiResult.updatedBalance !== undefined) {
        console.log('💰 Backend reported updated balance:', apiResult.updatedBalance);
      } else {
        console.warn('⚠️ Backend did not return updated balance - potential balance sync issue');
      }
      
      // Emit balance update event for immediate UI update
      // Only emit for cash tokens (not invoices) since invoices don't deduct balance immediately
      if (effectiveTokenType === 'cash' && newToken.amount && newToken.currency) {
        // Use overrideBalance if provided (e.g., after PayPal top-up), otherwise use balances prop
        // Use the token's currency (which respects overrideCurrency) for balance lookup
        const balanceSource = balances || user?.balances || {};
        const currentBalance = overrideBalance !== undefined 
          ? overrideBalance 
          : (balanceSource[newToken.currency] || 0);
        console.log('💰 Emitting balance update event for token creation:', {
          tokenKey: newToken.key,
          amount: newToken.amount,
          currency: newToken.currency,
          currentBalance,
          usingOverride: overrideBalance !== undefined,
          balanceSource: overrideBalance !== undefined ? 'override' : (balanceSource === balances ? 'prop' : 'user context')
        });
        emitTokenCreationBalanceUpdate(
          newToken.amount,
          newToken.currency,
          currentBalance,
          newToken.key
        );
      }
      
      // If this was a referral token, update the local count
      if (isReferralToken && apiResult.referralInfo) {
        setReferralTokensCreated(prev => prev + 1);
      }
      
      setInternalIsSubmitting(false);
      
      // Reset form fields after successful creation
      setAmount('');
      setMessage('');
      setIsReferralToken(false);
      setIncludeAddress(false);
      setSelectedAddressId('');
      setShowConditioning(false);
      setTokenConditions(null);
      
      // NOTE: Don't reset tokenCreatedRef here - we want to prevent duplicate PayPal processing
      // It will be reset when user explicitly starts a new token creation (form submission)
      
      // CRITICAL: Mark that a token was created (even if showTokenAfterCreation=false)
      // This prevents PayPal return handler from trying to create another token
      tokenCreatedRef.current = true;
      
      // CRITICAL: Clear localStorage after successful token creation
      try {
        localStorage.removeItem('pendingTokenCreation');
        console.log('🧹 Cleared pending token creation from localStorage after successful creation');
      } catch (e) {
        console.warn('⚠️ Failed to clear localStorage:', e);
      }
      
      // CRITICAL: Clean up PayPal params immediately after successful token creation
      // This ensures PayPal params are removed regardless of which page uses CreateCashToken
      // Check if PayPal params exist (not just if token was created from PayPal)
      // This handles edge cases where params might still be present
      const currentParams = new URLSearchParams(location.search);
      const hasPayPalParams = currentParams.has('paypal') || currentParams.has('context');
      if (hasPayPalParams || tokenCreatedFromPayPalRef.current) {
        cleanupPayPalParams();
        tokenCreatedFromPayPalRef.current = false;
      }
      
      // Store token internally if we're showing it ourselves
      if (showTokenAfterCreation) {
        setCreatedToken(newToken);
      }
      
      // CRITICAL: Emit token:created event ONCE (prevent duplicates)
      // This event is emitted in all cases, whether or not there's a parent window
      if (!tokenCreatedEventEmittedRef.current) {
        console.log('🎫 Emitting token:created event for token:', newToken.key);
        emitTokenCreated({
          token: {
            key: newToken.key,
            amount: newToken.amount,
            currency: newToken.currency,
            message: newToken.message,
            type: newToken.type,
            ...newToken // Include all token properties
          }
        });
        tokenCreatedEventEmittedRef.current = true;
        
        // If postToParent is enabled, also post message to parent window
        // This happens as part of the same event emission to prevent duplicates
        if (detectedPostToParent && window.parent && window.parent !== window) {
          console.log('🔍 Posting message to parent window with token:', newToken.key);
          try {
            window.parent.postMessage(
              { 
                type: "vaiu", 
                token: newToken.key,
                amount: newToken.amount,
                currency: newToken.currency,
                message: newToken.message
              },
              "*"
            );
            console.log('✅ Successfully posted token to parent window');
          } catch (error) {
            console.error('❌ Failed to post message to parent:', error);
          }
        }
      } else {
        console.log('⏭️ Skipping duplicate token:created event emission');
      }
      
      // Call onSubmit callback to notify parent (parent can still handle it if needed)
      onSubmit(newToken);
      
      return newToken;
    } catch (error: any) {
      console.error('❌ Token creation failed:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
        fullError: error
      });
      setInternalIsSubmitting(false);
      
      // Handle insufficient balance inline (no error toast — TopUpBalance UI is shown instead)
      // Note: localStorage is already set in handleSubmit before createToken is called
      if (error.message === 'insufficient_balance' || error.message?.includes('insufficient') || error.message?.includes('balance')) {
        console.log('💳 Insufficient balance detected during token creation');
        const tokenAmount = amount;
        // Use the currency that was attempted (respect overrideCurrency if used)
        const attemptedCurrency = overrideCurrency || effectiveCurrency;
        
        if (!isOwnMmMoneyCurrency(attemptedCurrency, authUserId)) {
          // Store pending token data in state
          setPendingTokenData({ 
            amount: tokenAmount, 
            message, 
            isReferralToken,
            isMultiUseToken: isMultiUseToken || isReferralToken,
            ...(parsedMaxUses ? { maxUses: parsedMaxUses } : {}),
          });
          setTopUpRequiredAmount(tokenAmount);
          setTopUpCurrency(attemptedCurrency);
          
          if (isGuest) {
            setShowInlinePayment(true);
            setPaymentStage('idle');
            setShowTopUpDialog(0);
          } else {
            setShowTopUpDialog(1);
          }
          
          return;
        }
      }
      
      toast({
        title: 'Token Creation Failed',
        description: error.message || 'Failed to create token. Please try again.',
        variant: 'destructive'
      });
      
      throw error;
    }
  }, [effectiveTokenType, typeOfToken, selectedCurrency, effectiveCurrency, isReferralToken, isBankUser, merchantId, isGuest, guestEmail, guestPhone, tokenConditions, includeAddress, selectedAddressId, userAddresses, balances, user, onSubmit, toast, extraTokenPayload]);

  // Update ref so attemptTokenCreationFromLocalStorage can use createToken
  useEffect(() => {
    createTokenRef.current = createToken;
  }, [createToken]);

  // handleSubmit - must be defined before useEffect hooks and JSX that use it
  const handleSubmit = useCallback(async (e: React.FormEvent, overrideAmount?: string) => {
    e.preventDefault();
    
    console.log('🚀 [handleSubmit] Form submitted', {
      overrideAmount,
      amount,
      message,
      selectedCurrency,
      isReferralToken,
      locationSearch: location.search
    });
    
    // CRITICAL: Reset token creation flag when user explicitly submits form
    // This allows creating a new token after closing the previous one
    // Only reset if this is a new submission (not a PayPal return)
    const searchParams = new URLSearchParams(location.search);
    const isPayPalReturn = searchParams.get('paypal') === 'complete' && searchParams.get('context')?.startsWith('topup:');
    if (!isPayPalReturn) {
      tokenCreatedRef.current = false;
    }
    
    // Use overrideAmount if provided (for PayPal return), otherwise use state
    const amountToUse = overrideAmount || amount;
    const rawAmount = parseFloat(amountToUse);
    const tokenAmount = Number.isNaN(rawAmount)
      ? NaN
      : roundMonetaryValue(rawAmount, amountDecimals);
    let tokenMessage = message;
    
    // Add address to message if selected (do this BEFORE storing in localStorage)
    if (includeAddress && selectedAddressId) {
      const selectedAddress = userAddresses.find(addr => addr.id === selectedAddressId);
      if (selectedAddress) {
        const addressText = selectedAddress.detail 
          ? `${selectedAddress.address}, ${selectedAddress.detail}`
          : selectedAddress.address;
        
        // Add address to message with clear formatting
        tokenMessage = tokenMessage 
          ? `${tokenMessage}\n\n📍 Delivery Address:\n${addressText}`
          : `📍 Delivery Address:\n${addressText}`;
      }
    }
    
    // Validate amount BEFORE storing in localStorage
    if (Number.isNaN(tokenAmount) || !tokenAmount || tokenAmount <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Please enter a valid amount greater than 0',
        variant: 'destructive'
      });
      return;
    }
    
    // CRITICAL: Store token specs in localStorage AFTER validation but BEFORE balance check
    // Only store for cash tokens (not invoice tokens) since invoices don't require balance
    // This ensures token data persists even if balance check fails or user is redirected
    // CRITICAL: This MUST happen for BOTH guests and authenticated users
    if (effectiveTokenType === 'cash') {
      try {
        const tokenDataToStore = {
          amount: tokenAmount,
          message: tokenMessage, // Use message with address if included
          currency: effectiveCurrency,
          isReferralToken,
          isMultiUseToken: isMultiUseToken || isReferralToken,
          ...(parsedMaxUses ? { maxUses: parsedMaxUses } : {}),
          timestamp: Date.now(),
          ...(showConditioning && tokenConditions
            ? { tokenConditions }
            : {}),
        };
        localStorage.setItem('pendingTokenCreation', JSON.stringify(tokenDataToStore));
        console.log('💾 [handleSubmit] Stored pending token creation in localStorage:', {
          ...tokenDataToStore,
          isGuest
        });
      } catch (e) {
        console.warn('⚠️ Failed to store pending token in localStorage:', e);
      }
    } else {
      console.log('⏭️ Skipping localStorage storage for invoice token (not needed)');
    }

    // Validate Path B platform referral (ADMIN/BANK only)
    if (isReferralToken) {
      if (!isBankUser) {
        toast({
          title: 'Not allowed',
          description: 'Only bank or admin users can create platform-funded referral tokens.',
          variant: 'destructive',
        });
        return;
      }

      if (referralTokensCreated >= 10) {
        toast({
          title: 'Referral Token Limit Reached',
          description: 'You have reached the maximum limit of 10 referral tokens. Please wait for some to be redeemed.',
          variant: 'destructive'
        });
        return;
      }
    }

    if (isMultiUseToken && !isReferralToken && !parsedMaxUses) {
      toast({
        title: 'Max Uses Required',
        description: 'Multi-use tokens need a maximum redemption count of at least 2.',
        variant: 'destructive',
      });
      return;
    }

    await createToken({ 
      amount: tokenAmount, 
      message: tokenMessage, 
      isReferralToken,
      isMultiUseToken: isMultiUseToken || isReferralToken,
      maxUses: parsedMaxUses,
    });
  }, [amount, amountDecimals, message, includeAddress, selectedAddressId, userAddresses, isReferralToken, isMultiUseToken, parsedMaxUses, maxUses, referralTokensCreated, balances, effectiveCurrency, isGuest, isBankUser, effectiveTokenType, selectedCurrency, createToken, toast, setPendingTokenData, setTopUpRequiredAmount, setTopUpCurrency, setShowInlinePayment, setPaymentStage, setShowTopUpDialog, showConditioning, tokenConditions, location.search, prepaidRequiredAmount]);

  // Handle PayPal return with token creation data
  // PayPal return handler - emits balance:update signal when PayPal returns
  // This MUST run for all users (including guests) to trigger token creation
  useEffect(() => {
    // CRITICAL: Log immediately to verify effect is running
    console.log('🔍 [PayPal Handler] useEffect STARTING - effect is running', {
      locationSearch: location.search,
      locationHash: location.hash,
      isGuest,
      hasCreatedToken: !!createdToken,
      tokenCreatedRef: tokenCreatedRef.current
    });
    
    // Initialize mountedRef early to prevent early returns in createToken
    mountedRef.current = true;
    
    const searchParams = new URLSearchParams(location.search);
    const paypalStatus = searchParams.get('paypal');
    const paypalContext = searchParams.get('context');
    
    // Debug: Log PayPal handler entry - ALWAYS log, not just when params exist
    console.log('🔍 [PayPal Handler] useEffect running:', {
      paypalStatus,
      paypalContext,
      hasCreatedToken: !!createdToken,
      tokenCreatedRef: tokenCreatedRef.current,
      locationSearch: location.search,
      isGuest,
      mountedRef: mountedRef.current
    });
    
    // Create a unique key for this PayPal return to prevent duplicate processing
    // Do this early so we can use it in multiple checks
    const paypalReturnKey = `${paypalStatus}-${paypalContext || ''}`;

    let pendingUnfundedKey = '';
    try {
      const storedPending = localStorage.getItem('pendingTokenCreation');
      if (storedPending) {
        pendingUnfundedKey = JSON.parse(storedPending)?.tokenKey || '';
      }
    } catch {
      pendingUnfundedKey = '';
    }
    const tokenAlreadyFunded =
      Boolean(createdToken && !isUnfundedTokenStatus(createdToken.status) && !pendingUnfundedKey);

    if (tokenAlreadyFunded && (paypalStatus || paypalContext)) {
      processedPayPalReturnRef.current = paypalReturnKey;
      cleanupPayPalParams();
      return;
    }
    
    // CRITICAL: Check sessionStorage FIRST to prevent duplicate processing on remount
    try {
      const stored = sessionStorage.getItem('paypalReturnProcessed');
      if (stored) {
        const parsed = JSON.parse(stored);
        // If processed recently (within 5 minutes), skip
        if (Date.now() - parsed.timestamp < 5 * 60 * 1000) {
          console.log('⏭️ [PayPal Handler] EARLY RETURN: Already processed (sessionStorage)', {
            paypalReturnKey,
            isGuest
          });
          // Still clean up URL if present
          if (paypalStatus === 'complete' && paypalContext) {
            cleanupPayPalParams();
          }
          return;
        }
      }
    } catch (e) {
      console.warn('⚠️ Failed to check sessionStorage:', e);
    }
    
    // Skip if we've already processed this PayPal return
    if (paypalReturnKey === processedPayPalReturnRef.current) {
      console.log('⏭️ [PayPal Handler] EARLY RETURN: Already processed (ref)', {
        paypalReturnKey,
        isGuest
      });
      return;
    }
    
    if (tokenCreatedRef.current && tokenAlreadyFunded) {
      processedPayPalReturnRef.current = paypalReturnKey;
      cleanupPayPalParams();
      return;
    }
    
    // CRITICAL: Prevent processing if already processing PayPal return (prevents duplicate processing)
    if (paypalProcessingRef.current) {
      console.warn('⚠️ [PayPal Handler] EARLY RETURN: Already processing', {
        paypalReturnKey,
        isGuest
      });
      return;
    }
    
    // CRITICAL: Prevent processing if already submitting (prevents race conditions)
    // BUT: Don't block if we're returning from PayPal - the payment already succeeded
    // Only block if we're actively creating a token (not just returning from payment)
    if (isSubmitting && !paypalStatus) {
      console.warn('⚠️ [PayPal Handler] EARLY RETURN: Already submitting (no PayPal status)', {
        isSubmitting,
        paypalStatus,
        isGuest
      });
      return;
    }
    
    // Mobile detection for debugging
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    console.log('📱 PayPal return handler:', {
      isMobile,
      userAgent: navigator.userAgent,
      paypalStatus,
      hasContext: !!paypalContext,
      locationSearch: location.search,
      locationHash: location.hash
    });
    
    // Check for PayPal return - PayPal handler ONLY emits balance:update event (no payload)
    // Both BalancesSelector and CreateCashToken listen for this event independently
    if (paypalStatus === 'complete') {
      console.log('💳 [PayPal Handler] PayPal return detected, emitting balance:update signal:', { 
        hasContext: !!paypalContext,
        isMobile,
        currentUrl: window.location.href,
        timestamp: new Date().toISOString(),
        isGuest,
        paypalReturnKey
      });
      
      // Mark this return as processed to prevent duplicate processing
      processedPayPalReturnRef.current = paypalReturnKey;

      const contextKey = paypalContext?.includes('tokenKey=')
        ? new URLSearchParams(paypalContext.replace(/^topup:/, '').replace(/^create-token:/, '')).get('tokenKey')
        : '';
      const keyToRefresh = pendingUnfundedKey || contextKey;
      if (keyToRefresh) {
        void refreshCreatedToken(keyToRefresh);
      }

      const sessionId = readPayPalSessionId(location.search);
      if (isPayPalFundingReturnTab(location.search) && sessionId) {
        publishPayPalFundingResult({
          type: 'vaiu-paypal-funded',
          sessionId,
          tokenKey: keyToRefresh || undefined,
        });
      }

      emitBalanceUpdateSignal();
      cleanupPayPalParams();
    } else if (paypalStatus === 'cancelled' && isPayPalFundingReturnTab(location.search)) {
      const sessionId = readPayPalSessionId(location.search);
      if (sessionId) {
        publishPayPalFundingResult({ type: 'vaiu-paypal-cancelled', sessionId });
      }
    } else {
      console.log('🔍 [PayPal Handler] No PayPal return detected (paypalStatus !== "complete")', {
        paypalStatus,
        paypalContext
      });
    }
    
    // Cleanup function: Clean up PayPal params on unmount if they're still present
    // This ensures cleanup happens even if component unmounts before token is created
    return () => {
      const currentSearch = location.search;
      // If PayPal params are still present on unmount, clean them up
      // This prevents stale params from causing issues if user navigates away
      if (currentSearch.includes('paypal=complete') || currentSearch.includes('context=')) {
        console.log('🧹 [CreateCashToken] Cleaning up PayPal params on unmount');
        cleanupPayPalParams();
      }
      
      // Reset refs (but keep tokenCreatedRef - token was already created)
      autoSubmitAttemptedRef.current = false;
      processedPayPalReturnRef.current = '';
      paypalProcessingRef.current = false;
      tokenCreatedFromPayPalRef.current = false;
      // NOTE: Don't reset tokenCreatedRef here - token was already created, we want to prevent duplicates
    };
    // CRITICAL: Remove selectedCurrency from dependencies - it causes re-runs with wrong currency
    // CRITICAL: Include isGuest in dependencies to ensure effect runs for guest users
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.hash, cleanupPayPalParams, createdToken, isGuest]); // Include createdToken to detect stale params, isGuest to ensure guest handling

  // Listen for balance:update event - fetch fresh balances and process localStorage token creation
  // This is independent of BalancesSelector - both components react to the same event
  // Uses the same reusable function as mount logic for consistency
  useEffect(() => {
    console.log('🔌 [balance:update] Setting up listener in CreateCashToken');
    
    const unsubscribe = onBalanceUpdate(async () => {
      console.log('💰 [balance:update] Event received in CreateCashToken - starting handler');
      await attemptTokenCreationFromLocalStorage('balance:update');
    });

    return unsubscribe;
  }, [attemptTokenCreationFromLocalStorage]);

  // Auto-submit effect
  useEffect(() => {
    mountedRef.current = true;

    const attemptAutoSubmit = async () => {
      // CRITICAL: Skip auto-submit if PayPal return handler is processing
      // This prevents duplicate token creation when PayPal return sets amount state
      const searchParams = new URLSearchParams(location.search);
      const paypalStatus = searchParams.get('paypal');
      const paypalContext = searchParams.get('context');
      if (paypalStatus === 'complete' && paypalContext && paypalContext.startsWith('topup:')) {
        console.log('⏭️ PayPal return detected, skipping auto-submit (PayPal handler will process)');
        return;
      }
      
      // CRITICAL: Prevent auto-create token for guest users
      // Guest users should always see the payment UI first, never auto-create tokens
      if (isGuest) {
        console.log('⏭️ Guest user detected, skipping auto-submit (guest users should see payment UI first)');
        return;
      }
      
      if (
        autoSubmit &&
        amount &&
        parseFloat(amount) > 0 &&
        !autoSubmitAttemptedRef.current
      ) {
        console.log('🔄 Auto-submitting token creation with amount:', amount);
        autoSubmitAttemptedRef.current = true;
        
        // Small delay to ensure component is fully mounted
        setTimeout(async () => {
          if (mountedRef.current) {
            await createToken({
              amount: roundMonetaryValue(parseFloat(amount), amountDecimals),
              message,
              isReferralToken: false,
            });
          }
        }, 100);
      }
    };

    attemptAutoSubmit();

    return () => {
      mountedRef.current = false;
    };
  }, [autoSubmit, amount, amountDecimals, message, location.search, isGuest, createToken, isReferralToken]);

  // Sync selectedCurrency prop with internal currency state
  useEffect(() => {
    if (selectedCurrency !== currency) {
      setCurrency(selectedCurrency);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCurrency]); // Only sync when prop changes, not when internal state changes

  // Handle TopUpBalance completion - SECURE IMPLEMENTATION
  const handleTopUpSuccess = async (newBalance: number, fundingDetails?: any) => {
    console.log('💰 [CreateCashToken] TopUp callback triggered with reported balance:', newBalance);
    console.log('💳 [CreateCashToken] Funding details:', fundingDetails);
    console.log('🔍 [CreateCashToken] Current state:', {
      isGuest,
      showInlinePayment,
      pendingTokenData,
      amount,
      message,
      paymentStage
    });
    setShowTopUpDialog(0);
    
    // Update payment stage for inline flow
    if (showInlinePayment) {
      setPaymentStage('idle'); // Reset to idle - user must manually retry
    }
    
    // TopUpBalance emits balance:update after a successful funding operation.
    // The listener above fetches the confirmed balance and creates the pending token.
    console.log('✅ Top-up completed. Creating the pending token via the balance-update flow.');
  };

  // Handle TopUpBalance cancellation
  const handleTopUpCancel = () => {
    console.log('❌ TopUp cancelled by user');
    setShowTopUpDialog(0);
    setPendingTokenData(null);
    setTopUpRequiredAmount(0);
    setSelectedFundingSource('card');
    setTopUpInitialTab('card');
    
    // Clear localStorage as well
    try {
      localStorage.removeItem('pendingTokenCreation');
      console.log('🧹 Cleared pending token creation from localStorage (cancelled)');
    } catch (e) {
      console.warn('⚠️ Failed to clear localStorage:', e);
    }
  };



  const currentBalance = spendableBalance(effectiveCurrency);
  const requiredCover = prepaidRequiredAmount > 0 ? prepaidRequiredAmount : (amountRounded ?? 0);
  const shouldShowFundingChoices =
    !isBankUser && effectiveTokenType === 'cash';
  const needsFunding =
    shouldShowFundingChoices && (selectedFundingSource !== 'balance' || requiredCover > currentBalance);

  // Calculate referral reward potential
  const rewardPotential = Math.floor((amountRounded ?? 0) / 10);

  // Debug balance data for troubleshooting
  if (showTopUpDialog) {
    console.log('🔍 TopUpBalance Debug:', {
      isBankUser,
      topUpCurrency,
      currentBalance: balances[topUpCurrency] || 0,
      allBalances: balances,
      requiredAmount: topUpRequiredAmount
    });
  }


  // Handle token dismissal - clean up PayPal params and reset state
  const handleTokenDismiss = useCallback(() => {
    console.log('🧹 [CreateCashToken] Token dismissed, cleaning up');
    setCreatedToken(null);
    tokenCreatedRef.current = false; // Reset token creation flag
    
    // CRITICAL: Clean up PayPal params when token is dismissed
    // This prevents re-triggering token creation if user closes token card
    cleanupPayPalParams();
    
    // Notify parent if callback provided
    onTokenDismiss?.();
  }, [cleanupPayPalParams, onTokenDismiss]);

  // Always show the creation form
  const recentToken = showTokenAfterCreation ? createdToken : null;

  return (
    <div className="w-full max-w-full min-w-0 space-y-3">
      {isPayPalFundingReturnTab(location.search) ? (
        <p className="text-sm text-muted-foreground">
          PayPal finished in this tab. You can close it; the original page will update.
        </p>
      ) : null}
      {recentToken ? (
        <CreatedTokenResult
          token={recentToken}
          theme={tokenTheme}
          onDismiss={handleTokenDismiss}
          showQRCode={false}
        />
      ) : null}

      <div className={recentToken ? 'hidden' : undefined}>
      {/* Token mode dropdown lives in accordion header when hideTypeSelector is set */}
      {showTypeSlider && !hideTypeSelector && (
        <div className="mb-4">
          <TokenModeSelect value={tokenMode} onValueChange={handleTokenModeChange} triggerClassName="w-full" />
          {/* Slider UI commented out - replaced by dropdown above
          <TokenTypeSlider
            value={internalTokenType}
            onChange={(newType) => {
              setInternalTokenType(newType);
              onTypeChange?.(newType);
              if (newType === 'invoice') {
                setIsReferralToken(false);
              }
            }}
            isMerchant={isMerchant}
            isPersonalMode={isPersonalMode}
          />
          */}
        </div>
      )}

      {/* ID Token creation - when user selects ID Token from dropdown */}
      {showTypeSlider && tokenMode === 'id' ? (
        <CreateIDToken
          onSubmit={(idToken) => {
            onSubmit({
              key: idToken.key,
              amount: 0,
              currency: '',
              status: idToken.status,
              expiry: idToken.expiry,
              type: 'identity',
            } as Token);
          }}
          availableCurrencies={Object.keys(balances).length > 0 ? Object.keys(balances) : ['USD', 'EUR', 'GBP']}
        />
      ) : (
      <>
      <form id="create-cash-token-form" onSubmit={handleSubmit} className="space-y-3">
        {/* Guest receipt contact info - only show if there's a payeeId (merchantId) */}
        {isGuest && merchantId && (
          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 space-y-2">
            <div>
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {payeeName ? i18n.__('createToken.paymentTo', { name: payeeName }) : i18n.__('createToken.payment')}
              </p>
              <p className="text-xs text-blue-800 dark:text-blue-200 mt-1">
                {i18n.__('createToken.receiptPrompt')}
              </p>
            </div>
            <Input 
              type="email" 
              placeholder={i18n.__('createToken.emailPlaceholder')}
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              className="text-sm"
            />
            <Input 
              type="tel" 
              placeholder={i18n.__('createToken.phonePlaceholder')}
              value={guestPhone}
              onChange={(e) => setGuestPhone(e.target.value)}
              className="text-sm"
            />
          </div>
        )}

        <div>
          {effectiveTokenType === 'invoice' ? (
            <Label htmlFor="amount">
              {i18n.__('vaiu.token_form.amount_to_receive')}
            </Label>
          ) : (
            <Label htmlFor="amount" className="sr-only">
              {i18n.__('vaiu.token_slider.create_token')}
            </Label>
          )}
          <div className="relative flex items-center justify-center">
            <div className="flex items-baseline gap-2">
              <input 
                id="amount" 
                type="number"
                inputMode={amountDecimals <= 0 ? 'numeric' : 'decimal'}
                step={decimalsToHtmlStep(amountDecimals)}
                min={amountDecimals <= 0 ? '0' : '0.01'}
                max="999999999"
                placeholder="0"
                value={amount}
                onChange={(e) => {
                  setAmount(sanitizeAmountInput(e.target.value, amountDecimals));
                }}
                onWheel={(e) => {
                  e.currentTarget.blur();
                  e.stopPropagation();
                }}
                required
                disabled={isSubmitting}
                className="bg-transparent border-none outline-none text-center text-6xl font-bold text-gray-900 dark:text-white w-full max-w-[240px] placeholder-gray-400 dark:placeholder-gray-500 focus:ring-0 appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none transition-all duration-200"
                style={{
                  fontSize: amount && amount.length > 4 ? '3rem' : '3.75rem',
                  letterSpacing: '0.025em'
                }}
              />
              {canSelectTokenCurrency ? (
                <select
                  aria-label="Currency"
                  value={effectiveCurrency}
                  onChange={(e) => {
                    const nextCurrency = e.target.value;
                    setCurrency(nextCurrency);
                    setTopUpInitialTab('card');
                    setPendingTokenData(null);
                    setTopUpRequiredAmount(0);
                    setTopUpCurrency(nextCurrency);
                    try {
                      localStorage.removeItem('pendingTokenCreation');
                    } catch {
                      /* ignore */
                    }
                  }}
                  className="text-2xl font-bold text-gray-600 dark:text-gray-400 ml-2 bg-transparent border-none outline-none cursor-pointer hover:text-gray-800 dark:hover:text-gray-200"
                >
                  {selectableCurrencies.map(curr => {
                    const mmIssuer = parseMmMoneyIssuerId(curr);
                    const mmLabel = isOwnMmMoneyCurrency(curr, authUserId)
                      ? formatMmMoneyDisplayTicker()
                      : mmIssuer
                        ? `${formatMmMoneyDisplayTicker()} · ${mmIssuer.slice(-4)}`
                        : curr;
                    return (
                      <option key={curr} value={curr}>
                        {isMmMoneyCurrency(curr) ? mmLabel : curr}
                      </option>
                    );
                  })}
                </select>
              ) : isMmMoneyCurrency(effectiveCurrency) && !isOwnMmMoneyCurrency(effectiveCurrency, authUserId) ? (
                <div className="ml-2 min-w-0 max-w-[200px] flex-shrink-0">
                  <MmMoneyIssuerDisplay
                    currencyKey={effectiveCurrency}
                    issuer={selectedMmIssuerMeta}
                    fetchMerchantSummary={fetchMmMoneyMerchantSummary}
                    compact
                    hideProfileAction
                    className="min-w-0"
                  />
                </div>
              ) : (
                <span className="text-2xl font-bold text-gray-600 dark:text-gray-400 ml-2">
                  {amountFieldCurrencyLabel ?? effectiveCurrency}
                </span>
              )}
            </div>
          </div>
          {/* Show inline balance warning if amount exceeds balance */}
          {!isGuest && !isBankUser && effectiveTokenType !== 'invoice' && selectedFundingSource === 'balance' && prepaidRequiredAmount > 0 && prepaidRequiredAmount > spendableBalance(effectiveCurrency) && (
            <div className="space-y-2">
              <div className="text-xs text-orange-600 flex items-center gap-1 mt-1">
                <InfoIcon className="h-3 w-3" />
                <span>
                  Insufficient currency balance
                </span>
              </div>
              {/* Instant Bank Funding Option */}
              {featureEnabled && nfcAvailable && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full text-sm border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                  onClick={() => setShowCardToTokenDialog(true)}
                  disabled={isSubmitting}
                >
                  💳 {i18n.__('createToken.instantBankFunding')}
                </Button>
              )}
            </div>
          )}
          {effectiveTokenType === 'invoice' && (
            <p className="text-xs text-muted-foreground mt-1">
              {i18n.__('createToken.invoiceAmount')}
            </p>
          )}
        </div>
        
        <div>
          {!showMessageInput ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowMessageInput(true)}
              className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              Add message
            </Button>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="message">{i18n.__('common.message')} ({i18n.__('common.optional')})</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowMessageInput(false)}
                  className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  Hide
                </Button>
              </div>
              <textarea 
                id="message" 
                placeholder={i18n.__('createToken.messagePlaceholder')}
                value={message}
                onChange={(e) => {
                  if (e.target.value.length <= 1000) {
                    setMessage(e.target.value);
                  }
                }}
                disabled={isSubmitting}
                className={`w-full min-h-[70px] px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 resize-vertical ${embedMode ? 'text-base' : 'text-sm'}`}
                rows={2}
                maxLength={1000}
              />
              <div className="flex justify-end mt-1">
                <span className="text-xs text-muted-foreground">
                  {message.length}/1000
                </span>
              </div>
            </>
          )}
        </div>
        
        {/* Advanced Options Toggle - Only show for cash tokens */}
        {effectiveTokenType === 'cash' && !isGuest && (
          <div className="space-y-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 p-0"
              onClick={() => {
                setShowAdvancedOptions(!showAdvancedOptions);
                if (showAdvancedOptions) {
                  setIsReferralToken(false);
                  setIsMultiUseToken(false);
                  setMaxUses('');
                }
              }}
            >
              {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              {i18n.__('vaiu.token_form.advanced_options')}
            </Button>
            
            {/* Advanced Options Content */}
            {showAdvancedOptions && (
              <div className="space-y-4 pl-6 animate-in slide-in-from-top-2 duration-200">
                {/* Address Selection */}
                {userAddresses.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="includeAddress"
                        checked={includeAddress}
                        onCheckedChange={(checked) => setIncludeAddress(!!checked)}
                        disabled={isSubmitting || userAddresses.length === 0}
                      />
                      <Label 
                        htmlFor="includeAddress" 
                        className="text-sm cursor-pointer flex items-center gap-2"
                      >
                        <MapPin className="w-4 h-4" />
                        {i18n.__('createToken.includeAddress')}
                      </Label>
                    </div>
                    
                    {includeAddress && (
                      <div className="pl-6 space-y-2">
                        <Select
                          value={selectedAddressId}
                          onValueChange={setSelectedAddressId}
                          disabled={isSubmitting}
                        >
                          <SelectTrigger className="w-full text-sm">
                            <SelectValue placeholder={i18n.__('createToken.selectAddress')} />
                          </SelectTrigger>
                          <SelectContent>
                            {userAddresses.map((address) => (
                              <SelectItem key={address.id} value={address.id}>
                                <div className="flex items-center gap-2">
                                  {address.isPrimary && <Home className="w-3 h-3" />}
                                  <span>
                                    {address.address}
                                    {address.detail && ` (${address.detail})`}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        {selectedAddressId && (
                          <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                            {i18n.__('createToken.addressAddedHint')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Multi-use token */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="multiUseToken"
                      checked={isMultiUseToken || isReferralToken}
                      onCheckedChange={(checked) => setIsMultiUseToken(!!checked)}
                      disabled={isSubmitting || isReferralToken}
                    />
                    <Label
                      htmlFor="multiUseToken"
                      className={`text-sm font-medium ${isReferralToken ? 'text-muted-foreground' : ''}`}
                    >
                      {i18n.__('createToken.multiUseToken')}
                    </Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-sm">
                          <div className="space-y-2 text-sm">
                            <p className="font-semibold">{i18n.__('createToken.multiUseTokenTooltip.title')}</p>
                            <p>{i18n.__('createToken.multiUseTokenTooltip.description')}</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  {(isMultiUseToken || isReferralToken) && !isReferralToken ? (
                    <div className="pl-6 space-y-1">
                      <Label htmlFor="maxUses" className="text-xs text-muted-foreground">
                        {i18n.__('createToken.maxUsesLabel')}
                      </Label>
                      <Input
                        id="maxUses"
                        type="number"
                        min={2}
                        step={1}
                        inputMode="numeric"
                        placeholder={i18n.__('createToken.maxUsesPlaceholder')}
                        value={maxUses}
                        onChange={(e) => setMaxUses(e.target.value.replace(/\D/g, ''))}
                        disabled={isSubmitting}
                        className="max-w-[10rem]"
                      />
                      <p className="text-xs text-muted-foreground">
                        {i18n.__('createToken.maxUsesHint')}
                      </p>
                      {amountRounded != null && parsedMaxUses ? (
                        <div className="text-sm bg-muted/50 rounded-md p-2 space-y-1">
                          <p>
                            Face value: <strong>{amountRounded}</strong> {effectiveCurrency}
                          </p>
                          <p>
                            Max redemptions: <strong>{parsedMaxUses}</strong>
                          </p>
                          <p>
                            Amount to reserve:{' '}
                            <strong>
                              {prepaidRequiredAmount} {effectiveCurrency}
                            </strong>
                          </p>
                          {isBankUser ? (
                            <p className="text-xs text-muted-foreground">
                              Bank/admin accounts are not balance-gated (platform cover).
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                {/* Path A tip for consumers; Path B create only for BANK/ADMIN */}
                {pathAPolicyEnabled && pathAPolicyHint ? (
                  <p className="text-xs text-muted-foreground pl-1">{pathAPolicyHint}</p>
                ) : null}

                {isBankOrAdminUser ? (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="referralToken"
                        checked={isReferralToken}
                        onCheckedChange={(checked) => setIsReferralToken(!!checked)}
                        disabled={isSubmitting || referralTokensCreated >= 10}
                      />
                      <Label
                        htmlFor="referralToken"
                        className={`text-sm font-medium ${referralTokensCreated >= 10 ? 'text-muted-foreground' : ''}`}
                      >
                        Platform referral token (funded by platform)
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground pl-6">
                      ADMIN/BANK only. Creates a magically funded token listed under Profile → Referral
                      Tokens (redeemer face + giver reward). Prefer the Admin Referral console for full
                      controls.
                    </p>
                    {loadingReferralLimits ? (
                      <span className="text-xs text-muted-foreground">
                        {i18n.__('createToken.loadingReferralLimits')}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {i18n.__('createToken.referralTokensCreated', { created: referralTokensCreated })}
                      </span>
                    )}
                  </div>
                ) : null}

                {/* Token Conditioning Checkbox */}
                <div className="flex items-center space-x-2 mt-4">
                  <Checkbox
                    id="tokenConditioning"
                    checked={showConditioning}
                    onCheckedChange={(checked) => {
                      const on = !!checked;
                      setShowConditioning(on);
                      if (on) {
                        setTokenConditions(DEFAULT_TOKEN_CONDITIONS);
                      } else {
                        setTokenConditions(null);
                      }
                    }}
                    disabled={isSubmitting}
                  />
                  <Label 
                    htmlFor="tokenConditioning" 
                    className="text-sm font-medium"
                  >
                    {i18n.__('vaiu.token_form.add_conditions')}
                  </Label>
                  
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm">
                        <div className="space-y-2 text-sm">
                          <p className="font-semibold">{i18n.__('createToken.acceptanceConditionsTooltip.title')}</p>
                          <p>{i18n.__('createToken.acceptanceConditionsTooltip.description')}</p>
                          <ul className="list-disc pl-4">
                            <li>{i18n.__('createToken.acceptanceConditionsTooltip.time')}</li>
                            <li>{i18n.__('createToken.acceptanceConditionsTooltip.recipient')}</li>
                            <li>{i18n.__('createToken.acceptanceConditionsTooltip.location')}</li>
                            <li>{i18n.__('createToken.acceptanceConditionsTooltip.escrow')}</li>
                          </ul>
                          <p className="text-amber-600">{i18n.__('createToken.acceptanceConditionsTooltip.escrowNote')}</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                {/* Show TokenConditioning component inline if checkbox is checked */}
                {showConditioning && (
                  <div className="mt-4">
                    <TokenConditioning
                      tokenAmount={amountRounded ?? 0}
                      tokenCurrency={effectiveCurrency}
                      tokenType={effectiveTokenType}
                      inline={true}
                      toast={toast}
                      onConditionsChange={setTokenConditions}
                      updateTokenConditions={(key, body) =>
                        platformApi.tokens.updateTokenConditions(key, body)
                      }
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* For guests with an amount: show funding UI instead of button */}
        
        
      
        
              {!isGuest && (effectiveTokenType !== 'cash' || isBankUser) && (() => {
          // Show the submit button only when creating from a sufficient currency balance.
          const currentBalance = spendableBalance(effectiveCurrency);
          const requiredCover = prepaidRequiredAmount > 0 ? prepaidRequiredAmount : (amountRounded ?? 0);
          const shouldShowFundingChoices = !isBankUser && effectiveTokenType === 'cash';
          
          if (shouldShowFundingChoices) {
            return null;
          }
          
          return (
            <div className="flex space-x-2 pt-1">
              <Button type="submit" className="flex-1" disabled={isSubmitting}>
                {isSubmitting ? i18n.__('createToken.creating') : 
                 payeeName ? i18n.__('common.give_money_to').replace('{{name}}', payeeName) :
                 merchantId ? i18n.__('common.give_money_to_stranger') :
                 isReferralToken ? i18n.__('createToken.createReferralToken') :
                 (isMultiUseToken || isReferralToken) ? i18n.__('createToken.createMultiUseToken') : `Create`}
              </Button>
              {onCancel && (
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('🔍 Cancel button clicked in CreateCashToken');
                    onCancel();
                  }} 
                  disabled={isSubmitting}
                >
                  {i18n.__('common.cancel')}
                </Button>
              )}
            </div>
          );
        })()}

        
      </form>
      </>
      )}
      {/* Funding sources belong exclusively to cash-token creation. */}
      {(!showTypeSlider || tokenMode !== 'id') && (() => {
        const currentBalance = spendableBalance(effectiveCurrency);
        const requiredCover = prepaidRequiredAmount > 0 ? prepaidRequiredAmount : (amountRounded ?? 0);
        // For guests, balance is 0, so any positive cash-token amount needs a funding source.
        // BANK/ADMIN: infinite money — never show top-up for cover
        const shouldShowFundingChoices =
          !isBankUser && effectiveTokenType === 'cash';

        if (shouldShowFundingChoices) {
          return (
            <div className="space-y-4">
              {/* Processing Stage Indicator - show for guests during payment flow */}
              {isGuest && paymentStage !== 'idle' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex space-x-2">
                      <div className={`flex items-center space-x-2 ${paymentStage === 'funding' || paymentStage === 'creating-token' || paymentStage === 'complete' ? 'text-green-600' : 'text-gray-400'}`}>
                        {paymentStage === 'funding' ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : paymentStage === 'creating-token' || paymentStage === 'complete' ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : (
                          <Circle className="w-4 h-4" />
                        )}
                        <span className="text-sm font-medium">Processing Payment</span>
                      </div>
                      <span className="text-gray-400">→</span>
                      <div className={`flex items-center space-x-2 ${paymentStage === 'creating-token' ? 'text-blue-600' : paymentStage === 'complete' ? 'text-green-600' : 'text-gray-400'}`}>
                        {paymentStage === 'creating-token' ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : paymentStage === 'complete' ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : (
                          <Circle className="w-4 h-4" />
                        )}
                        <span className="text-sm font-medium">Creating Token</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment UI - show when idle or funding (for guests) or always (for authenticated users) */}
              {(!isGuest || paymentStage === 'idle' || paymentStage === 'funding') && (
                <StripeProvider>
                  <div className="w-full max-w-full min-w-0">
                  <TopUpBalance
                    currentBalance={currentBalance}
                    requiredAmount={requiredCover}
                    currency={effectiveCurrency}
                    onSuccess={handleTopUpSuccess}
                    onCancel={handleTopUpCancel}
                    isOpen={true}
                    initialTab={topUpInitialTab}
                    lockedTab={topUpInitialTab}
                    selectedFundingSource={selectedFundingSource}
                    onFundingSourceChange={(source) => {
                      setSelectedFundingSource(source);
                      setTopUpInitialTab(source === 'paypal' ? 'paypal' : ['wallet', 'bitcoin', 'ethereum'].includes(source) ? 'digital-wallet' : 'card');
                    }}
                    balances={balances}
                    amountDecimals={amountDecimals}
                    balanceSubmitFormId="create-cash-token-form"
                    context={{
                      type: 'token-creation',
                      returnUrl: buildFullReturnUrl(),
                      tokenAmount: requiredCover,
                      tokenCurrency: effectiveCurrency,
                      tokenMessage: message || '',
                      tokenKey: createdToken?.key || pendingTokenData?.tokenKey,
                    }}
                  />
                  </div>
                </StripeProvider>
              )}
            </div>
          );
        }
        
        // Keep the legacy fallback cash-only so invoice tokens never expose funding.
        if (effectiveTokenType === 'cash' && showTopUpDialog > 0) {
          return (
            <StripeProvider>
              <div className="w-full max-w-full min-w-0">
              <TopUpBalance
                currentBalance={balances[topUpCurrency] || 0}
                requiredAmount={topUpRequiredAmount}
                currency={topUpCurrency}
                onSuccess={handleTopUpSuccess}
                onCancel={handleTopUpCancel}
                isOpen={true}
                initialTab={topUpInitialTab}
                lockedTab={topUpInitialTab}
                context={{
                  type: 'token-creation',
                  returnUrl: buildFullReturnUrl(),
                  tokenAmount: topUpRequiredAmount,
                  tokenCurrency: topUpCurrency,
                  tokenMessage: pendingTokenData?.message || '',
                  tokenKey: createdToken?.key || pendingTokenData?.tokenKey,
                }}
              />
              </div>
            </StripeProvider>
          );
        }
        
        return null;
      })()}

      {/* Card-to-Token Dialog */}
      <Dialog open={showCardToTokenDialog} onOpenChange={setShowCardToTokenDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{i18n.__('createToken.instantBankFundingTitle')}</DialogTitle>
            <DialogDescription>
              {i18n.__('createToken.instantBankFundingDesc')}
            </DialogDescription>
          </DialogHeader>
          <CardToTokenFlow
            amount={prepaidRequiredAmount > 0 ? prepaidRequiredAmount : (amountRounded ?? 0)}
            currency={effectiveCurrency}
            onSuccess={async (sessionId, authCode) => {
              console.log('✅ Card-to-token funding successful:', { sessionId, authCode });
              
              // Refresh balance
              try {
                const updatedBalances = await platformApi.getBalances();
                // Update local balances state if available
                // Note: This might need to be passed up to parent component
                
                toast({
                  title: i18n.__('createToken.fundingSuccessful'),
                  description: i18n.__('createToken.fundingSuccessfulDesc'),
                });
                
                // Close dialog
                setShowCardToTokenDialog(false);
                
                // Retry token creation with the updated balance
                const raw = parseFloat(amount || '0');
                const tokenAmount = Number.isNaN(raw)
                  ? 0
                  : roundMonetaryValue(raw, amountDecimals);
                const tokenMessage = message;
                await createToken({ 
                  amount: tokenAmount, 
                  message: tokenMessage, 
                  isReferralToken,
                  isMultiUseToken: isMultiUseToken || isReferralToken,
                  maxUses: parsedMaxUses,
                });
              } catch (error: any) {
                console.error('Error refreshing balance after card-to-token:', error);
                toast({
                  title: i18n.__('createToken.balanceRefreshFailed'),
                  description: i18n.__('createToken.balanceRefreshFailedDesc'),
                  variant: 'destructive',
                });
                setShowCardToTokenDialog(false);
              }
            }}
            onCancel={() => setShowCardToTokenDialog(false)}
            onError={(error) => {
              console.error('Card-to-token error:', error);
              toast({
                title: i18n.__('createToken.fundingFailed'),
                description: error,
                variant: 'destructive',
              });
            }}
          />
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

// Export with both names for backward compatibility
export default CreateToken;
export { CreateToken as CreateCashToken }; 
